// ================================================================
// src/core/books/logic.js
// ================================================================
// لایه‌ی منطق کسب‌وکار سیستم کتاب‌های داینامیک
// کاملاً روی engine (storage) و dataEngine (entities) سوار می‌شه
// ----------------------------------------------------------------
// مسئولیت‌ها:
//   ۱. Notification Bus (pub/sub چند-مصرف‌کننده)
//   ۲. Audit Log (ثبت تغییرناپذیر همه‌ی اقدامات)
//   ۳. Rate Limiter (جلوگیری از abuse)
//   ۴. Permission Matrix (کنترل دسترسی)
//   ۵. Diff Engine (مقایسه‌ی نسخه‌ها)
//   ۶. Impact Analyzer (تحلیل تأثیر روی دانش‌آموزان)
//   ۷. Progress Manager (خواندن، نوشتن، مهاجرت پیشرفت)
//   ۸. Orphan Archive (نگه‌داری داده‌های یتیم)
//   ۹. Version Manager (عملیات سطح بالای نسخه)
//  ۱۰. Workflow Engine (گردش‌کار کامل درخواست/بروزرسانی)
//  ۱۱. Rollback Manager (بازگردانی به نسخه‌ی قبلی)
//  ۱۲. Facade متمرکز برای مصرف‌کننده‌ها
// ================================================================

import { engine, utils, constants } from './storage.js';
import dataEngine, {
  REQUEST_STATUS, PROJECT_STATE, VERSION_STATUS,
  PROJECT_TRANSITIONS, REQUEST_TRANSITIONS,
  normalizeGrade, normalizeSubject, normalizeField,
  createChapter, createLesson, createStep, createVideoRef,
  genProjectId, genVersionId, genChapterId, genLessonId, genStepId,
  getGrade, getSubject, getBookTitle, needsField,
} from './data.js';



import { api as _api } from './apiClient.js';




const { KEYS } = constants;
const { shortId, safeParse, safeStringify, estimateStringSize } = utils;
const now = () => Date.now();



// ================================================================
// CURRICULUM HELPERS (internal)
// ================================================================
// عنوان پایه — با fallback اگر شناسه ناشناخته بود
function gradeText(gradeId) {
  const g = getGrade(gradeId);
  return g?.title || `پایه ${gradeId || ''}`.trim();
}

// عنوان درس — بدون شماره کتاب
function subjectText(subjectId) {
  const s = getSubject(subjectId);
  return s?.title || subjectId || '';
}

// عنوان کامل کتاب — شامل شماره + رشته (برای ۱۰ تا ۱۲)
function bookText(gradeId, fieldId, subjectId) {
  return getBookTitle(gradeId, fieldId, subjectId) || subjectText(subjectId);
}



// ================================================================
// SECTION 1: CONSTANTS & CONFIG
// ================================================================

export const LOGIC_SCHEMA = '1.0.0';

export const PROGRESS_SCHEMA_VERSION = '2.0';

// TTL و Retention
const RETENTION = Object.freeze({
  NOTIFICATION_TTL_MS: 30 * 24 * 60 * 60 * 1000,     // ۳۰ روز
  AUDIT_TTL_MS: 90 * 24 * 60 * 60 * 1000,            // ۹۰ روز
  ORPHAN_TTL_MS: 90 * 24 * 60 * 60 * 1000,           // ۹۰ روز
  PROGRESS_BACKUP_KEEP: 5,                            // ۵ snapshot آخر
  AUDIT_MAX_ENTRIES: 2000,
  NOTIF_MAX_PER_USER: 100,
});

// Rate Limits (window در میلی‌ثانیه)
const RATE_LIMITS = Object.freeze({
  request_submit:       { max: 3, window: 24 * 60 * 60 * 1000 },
  request_withdraw:     { max: 5, window: 60 * 60 * 1000 },
  project_submit:       { max: 2, window: 60 * 60 * 1000 },
  version_update:       { max: 20, window: 60 * 60 * 1000 },
  video_upload:         { max: 10, window: 60 * 60 * 1000 },
  draft_save:           { max: 120, window: 60 * 1000 },
  admin_action:         { max: 60, window: 60 * 1000 },
});

// Notification types
export const NOTIF_TYPE = Object.freeze({
  REQUEST_SUBMITTED:      'request.submitted',
  REQUEST_APPROVED:       'request.approved',
  REQUEST_REJECTED:       'request.rejected',
  REQUEST_EDIT_REQUESTED: 'request.edit_requested',
  REQUEST_WITHDRAWN:      'request.withdrawn',
  VERSION_SUBMITTED:      'version.submitted',
  VERSION_APPROVED:       'version.approved',
  VERSION_REJECTED:       'version.rejected',
  VERSION_EDIT_REQUESTED: 'version.edit_requested',
  VERSION_PUBLISHED:      'version.published',
  PROJECT_SUSPENDED:      'project.suspended',
  PROJECT_RESUMED:        'project.resumed',
  PROJECT_DELETED:        'project.deleted',
  SYSTEM_ANNOUNCEMENT:    'system.announcement',
});

// Audit action names (constants برای جلوگیری از typo)
export const AUDIT_ACTION = Object.freeze({
  REQUEST_CREATE:           'request.create',
  REQUEST_SUBMIT:           'request.submit',
  REQUEST_APPROVE:          'request.approve',
  REQUEST_REJECT:           'request.reject',
  REQUEST_EDIT_REQUEST:     'request.edit_request',
  REQUEST_WITHDRAW:         'request.withdraw',
  REQUEST_EDIT:             'request.edit',
  TEACHER_CREATE:           'teacher.create',
  TEACHER_UPDATE:           'teacher.update',
  TEACHER_ARCHIVE:          'teacher.archive',
  TEACHER_RESTORE:          'teacher.restore',
  PROJECT_CREATE:           'project.create',
  PROJECT_UPDATE:           'project.update',
  PROJECT_PUBLISH:          'project.publish',
  PROJECT_SUSPEND:          'project.suspend',
  PROJECT_RESUME:           'project.resume',
  PROJECT_DELETE:           'project.delete',
  PROJECT_RESTORE:          'project.restore',
  VERSION_CREATE:           'version.create',
  VERSION_UPDATE:           'version.update',
  VERSION_SUBMIT:           'version.submit',
  VERSION_APPROVE:          'version.approve',
  VERSION_REJECT:           'version.reject',
  VERSION_EDIT_REQUEST:     'version.edit_request',
  VERSION_ROLLBACK:         'version.rollback',
  MIGRATION_RUN:            'migration.run',
  PROGRESS_MIGRATE:         'progress.migrate',
});

// Permission matrix
const PERMISSIONS = Object.freeze({
  'request.create':          ['teacher'],
  'request.submit':          ['teacher'],
  'request.withdraw':        ['teacher'],
  'request.approve':         ['admin'],
  'request.reject':          ['admin'],
  'request.edit_request':    ['admin'],
  'request.edit':            ['admin', 'teacher'],
  'teacher.update':          ['admin', 'teacher'],
  'teacher.archive':         ['admin'],
  'project.create':          ['teacher'],
  'project.update':          ['teacher', 'admin'],
  'project.delete':          ['admin'],
  'project.suspend':         ['admin'],
  'version.submit':          ['teacher'],
  'version.approve':         ['admin'],
  'version.reject':          ['admin'],
  'version.rollback':        ['admin'],
  'audit.read.all':          ['admin'],
  'audit.read.own':          ['teacher'],
});

// ================================================================
// SECTION 2: NOTIFICATION BUS
// ================================================================

class NotificationBus {
  constructor() {
    this.subscribers = new Map(); // userId → Set<callback>
    this.delayedQueue = [];
  }

  /** زیرنویس برای یک کاربر */
  subscribe(userId, callback) {
    if (!userId) return () => {};
    if (!this.subscribers.has(userId)) this.subscribers.set(userId, new Set());
    this.subscribers.get(userId).add(callback);
    return () => this.unsubscribe(userId, callback);
  }

  unsubscribe(userId, callback) {
    const set = this.subscribers.get(userId);
    if (set) set.delete(callback);
  }

  /** ارسال نوتیفیکیشن */
  async notify({ recipientId, type, payload = {}, title = '', body = '', actions = [], link = null }) {
    if (!recipientId) return null;

    const notif = {
      id: `nt_${shortId()}`,
      recipientId,
      type,
      title: title || this._defaultTitle(type),
      body: body || this._defaultBody(type, payload),
      payload,
      link: link || this._defaultLink(type, payload),
      actions: Array.isArray(actions) ? actions.slice(0, 3) : [],
      read: false,
      createdAt: now(),
      expiresAt: now() + RETENTION.NOTIFICATION_TTL_MS,
    };

    // ذخیره در localStorage
    const all = engine.get(KEYS.NOTIFICATIONS, {}) || {};
    if (!Array.isArray(all[recipientId])) all[recipientId] = [];
    all[recipientId].unshift(notif);

    // trim
    if (all[recipientId].length > RETENTION.NOTIF_MAX_PER_USER) {
      all[recipientId] = all[recipientId].slice(0, RETENTION.NOTIF_MAX_PER_USER);
    }
    engine.set(KEYS.NOTIFICATIONS, all);

    // broadcast به تب‌ها
    engine.broadcast('notif:new', { recipientId, notif });

    // emit به subscribers فعال
    this._dispatch(recipientId, notif);

    return notif;
  }

  _dispatch(userId, notif) {
    const subs = this.subscribers.get(userId);
    if (!subs) return;
    subs.forEach((cb) => {
      try { cb(notif); } catch (e) { console.warn('[Notif] callback error:', e); }
    });
  }

  /** لیست نوتیفیکیشن‌های یک کاربر */
  listFor(userId, { unreadOnly = false, limit = 50 } = {}) {
    const all = engine.get(KEYS.NOTIFICATIONS, {}) || {};
    let arr = Array.isArray(all[userId]) ? all[userId] : [];
    const cutoff = now();
    arr = arr.filter((n) => !n.expiresAt || n.expiresAt > cutoff);
    if (unreadOnly) arr = arr.filter((n) => !n.read);
    return arr.slice(0, limit);
  }

  /** تعداد خوانده‌نشده */
  unreadCount(userId) {
    return this.listFor(userId, { unreadOnly: true }).length;
  }

  /** علامت‌گذاری به‌عنوان خوانده‌شده */
  markRead(userId, notifId = null) {
    const all = engine.get(KEYS.NOTIFICATIONS, {}) || {};
    const arr = all[userId] || [];
    let changed = false;
    const ts = now();
    for (const n of arr) {
      if (notifId ? n.id === notifId : true) {
        if (!n.read) {
          n.read = true;
          n.readAt = ts;
          n.updatedAt = ts;  // 🆕 برای sync با سرور
          changed = true;
        }
      }
    }
    if (changed) {
      all[userId] = arr;
      engine.set(KEYS.NOTIFICATIONS, all);
      // 🆕 dispatch محلی — شمارنده در همین تب آپدیت می‌شود
      this._dispatch(userId, { type: 'read', notifId });
      // 🆕 broadcast بین تب‌ها
      engine.broadcast('notif:read', { userId, notifId });
    }
    return { ok: true, changed };
  }

  remove(userId, notifId) {
    const all = engine.get(KEYS.NOTIFICATIONS, {}) || {};
    const arr = all[userId] || [];
    const removed = arr.find((n) => n.id === notifId);
    all[userId] = arr.filter((n) => n.id !== notifId);
    engine.set(KEYS.NOTIFICATIONS, all);

    // 🆕 حذف کامل از دیتابیس سرور (از طریق صف حذف remoteSync)
    if (removed) {
      try { engine.emit('entity:deleted', { col: 'notifications', id: notifId }); } catch {}
    }

    // 🆕 dispatch محلی برای آپدیت شمارنده
    this._dispatch(userId, { type: 'removed', id: notifId });
    engine.broadcast('notif:removed', { userId, notifId });

    return { ok: true };
  }

  clear(userId) {
    const all = engine.get(KEYS.NOTIFICATIONS, {}) || {};
    const arr = all[userId] || [];
    const ids = arr.map((n) => n.id).filter(Boolean);
    all[userId] = [];
    engine.set(KEYS.NOTIFICATIONS, all);

    // 🆕 حذف کامل همه از سرور
    for (const id of ids) {
      try { engine.emit('entity:deleted', { col: 'notifications', id }); } catch {}
    }

    // 🆕 dispatch محلی
    this._dispatch(userId, { type: 'cleared' });
    engine.broadcast('notif:cleared', { userId });

    return { ok: true };
  }

  /** پاک‌سازی منقضی‌شده‌ها (اجرا در periodic cleanup) */
  purgeExpired() {
    const all = engine.get(KEYS.NOTIFICATIONS, {}) || {};
    const cutoff = now();
    let removed = 0;
    for (const uid of Object.keys(all)) {
      const before = all[uid].length;
      all[uid] = all[uid].filter((n) => !n.expiresAt || n.expiresAt > cutoff);
      removed += before - all[uid].length;
    }
    if (removed > 0) engine.set(KEYS.NOTIFICATIONS, all);
    return { ok: true, removed };
  }

  _defaultTitle(type) {
    const titles = {
      [NOTIF_TYPE.REQUEST_SUBMITTED]:      'درخواست معلمی جدید',
      [NOTIF_TYPE.REQUEST_APPROVED]:       'درخواست شما تایید شد',
      [NOTIF_TYPE.REQUEST_REJECTED]:       'درخواست شما رد شد',
      [NOTIF_TYPE.REQUEST_EDIT_REQUESTED]: 'نیاز به اصلاح درخواست',
      [NOTIF_TYPE.VERSION_SUBMITTED]:      'نسخه‌ی جدید برای بررسی',
      [NOTIF_TYPE.VERSION_APPROVED]:       'نسخه‌ی شما تایید و منتشر شد',
      [NOTIF_TYPE.VERSION_REJECTED]:       'نسخه‌ی شما رد شد',
      [NOTIF_TYPE.VERSION_EDIT_REQUESTED]: 'نسخه‌ی شما نیاز به اصلاح دارد',
      [NOTIF_TYPE.PROJECT_SUSPENDED]:      'پروژه معلق شد',
      [NOTIF_TYPE.PROJECT_RESUMED]:        'پروژه فعال شد',
    };
    return titles[type] || 'اطلاعیه';
  }

    _defaultBody(type, payload) {
    switch (type) {
      case NOTIF_TYPE.REQUEST_APPROVED: {
        const title = bookText(payload.grade, payload.field, payload.subject);
        return `می‌تونی حالا محتوای کتاب «${title}» رو بسازی.`;
      }
      case NOTIF_TYPE.REQUEST_REJECTED:
        return payload.reason ? `دلیل: ${payload.reason}` : 'متأسفانه درخواست شما پذیرفته نشد.';
      case NOTIF_TYPE.REQUEST_EDIT_REQUESTED:
        return payload.reason ? `مدیر این توضیح را داد: ${payload.reason}` : 'لطفاً درخواست خود را ویرایش کنید.';
      case NOTIF_TYPE.VERSION_APPROVED:
        return 'نسخه‌ی جدید شما برای دانش‌آموزان فعال شد.';
      case NOTIF_TYPE.VERSION_REJECTED:
        return payload.reason ? `دلیل: ${payload.reason}` : 'لطفاً نسخه را بازبینی کنید.';
      default:
        return '';
    }
  }

  _defaultLink(type, payload) {
    switch (type) {
      case NOTIF_TYPE.REQUEST_APPROVED:
      case NOTIF_TYPE.VERSION_APPROVED:
      case NOTIF_TYPE.VERSION_REJECTED:
      case NOTIF_TYPE.VERSION_EDIT_REQUESTED:
        return payload.projectId ? `/teacher/books/edit/${payload.projectId}` : '/teacher/books';
      case NOTIF_TYPE.REQUEST_REJECTED:
      case NOTIF_TYPE.REQUEST_EDIT_REQUESTED:
        return '/teacher/books';
      default:
        return null;
    }
  }
}

// ================================================================
// SECTION 3: AUDIT LOG
// ================================================================

class AuditLog {
  constructor() {
    this._buffer = [];
    this._flushTimer = null;
  }

  /**
   * ثبت یک رویداد
   * @param {object} entry - { actorId, actorRole, action, targetType, targetId, before, after, note, ip, userAgent }
   */
  record(entry) {
    if (!entry || !entry.action) return null;

    const record = {
      id: `au_${shortId()}`,
      at: now(),
      actorId: entry.actorId || null,
      actorRole: entry.actorRole || null,
      action: entry.action,
      targetType: entry.targetType || null,
      targetId: entry.targetId || null,
      before: this._sanitize(entry.before),
      after: this._sanitize(entry.after),
      note: entry.note ? String(entry.note).slice(0, 1000) : '',
      ip: entry.ip || null,
      userAgent: entry.userAgent || null,
    };

    // buffer برای کارایی
    this._buffer.push(record);
    this._scheduleFlush();

    return record;
  }

  _sanitize(obj) {
    if (obj == null) return null;
    try {
      // فقط snapshot بگیر، حجم بزرگ نکن
      const s = JSON.stringify(obj);
      if (estimateStringSize(s) > 50 * 1024) {
        return { _truncated: true, _size: s.length };
      }
      return JSON.parse(s);
    } catch {
      return null;
    }
  }

  _scheduleFlush() {
    if (this._flushTimer) return;
    this._flushTimer = setTimeout(() => this.flush(), 500);
  }

  flush() {
    if (this._buffer.length === 0) return;
    const toFlush = this._buffer.splice(0);
    const all = engine.get(KEYS.AUDIT_LOG, []) || [];
    all.push(...toFlush);

    // trim بر اساس TTL و max
    const cutoff = now() - RETENTION.AUDIT_TTL_MS;
    let trimmed = all.filter((e) => (e.at || 0) > cutoff);
    if (trimmed.length > RETENTION.AUDIT_MAX_ENTRIES) {
      trimmed = trimmed.slice(-RETENTION.AUDIT_MAX_ENTRIES);
    }

    engine.set(KEYS.AUDIT_LOG, trimmed, { silent: true });
    this._flushTimer = null;
  }

  /** خواندن لاگ‌ها */
  list({ actorId = null, targetId = null, action = null, limit = 100, since = null } = {}) {
    this.flush();
    let all = engine.get(KEYS.AUDIT_LOG, []) || [];
    if (actorId) all = all.filter((e) => e.actorId === actorId);
    if (targetId) all = all.filter((e) => e.targetId === targetId);
    if (action) {
      const actions = Array.isArray(action) ? action : [action];
      all = all.filter((e) => actions.includes(e.action));
    }
    if (since) all = all.filter((e) => (e.at || 0) >= since);
    return all.sort((a, b) => b.at - a.at).slice(0, limit);
  }

  count(filter = {}) { return this.list({ ...filter, limit: Infinity }).length; }

  clear() { engine.set(KEYS.AUDIT_LOG, []); }
}

// ================================================================
// SECTION 4: RATE LIMITER
// ================================================================

class RateLimiter {
  constructor() {
    this.buckets = new Map(); // `${actorId}:${action}` → number[] (timestamps)
  }

  _key(actorId, action) { return `${actorId}:${action}`; }

  /**
   * بررسی و ثبت یک تلاش
   * @returns {{ allowed: boolean, remaining: number, resetAt: number }}
   */
  check(actorId, action, opts = {}) {
    if (!actorId) return { allowed: false, reason: 'no_actor' };
    const limit = RATE_LIMITS[action];
    if (!limit) return { allowed: true, remaining: Infinity };

    const key = this._key(actorId, action);
    const nowMs = now();
    const windowStart = nowMs - limit.window;

    let bucket = this.buckets.get(key) || [];
    bucket = bucket.filter((t) => t > windowStart);

    if (bucket.length >= limit.max) {
      const oldest = bucket[0];
      return {
        allowed: false,
        remaining: 0,
        resetAt: oldest + limit.window,
        retryInMs: oldest + limit.window - nowMs,
      };
    }

    bucket.push(nowMs);
    this.buckets.set(key, bucket);
    return { allowed: true, remaining: limit.max - bucket.length };
  }

  peek(actorId, action) {
    const limit = RATE_LIMITS[action];
    if (!limit) return { remaining: Infinity };
    const key = this._key(actorId, action);
    const bucket = this.buckets.get(key) || [];
    const windowStart = now() - limit.window;
    const valid = bucket.filter((t) => t > windowStart);
    return { remaining: Math.max(0, limit.max - valid.length) };
  }

  reset(actorId, action) {
    if (action) this.buckets.delete(this._key(actorId, action));
    else {
      for (const k of Array.from(this.buckets.keys())) {
        if (k.startsWith(`${actorId}:`)) this.buckets.delete(k);
      }
    }
  }
}

// ================================================================
// SECTION 5: PERMISSION GUARD
// ================================================================

class PermissionGuard {
  can(actor, action, context = {}) {
    if (!actor) return false;
    const role = actor.role;
    if (!role) return false;

    const allowed = PERMISSIONS[action];
    if (!allowed) return false;
    if (!allowed.includes(role) && role !== 'admin') return false;

    // قوانین اضافه بر اساس context
    if (action === 'request.edit' && role === 'teacher') {
      const rq = dataEngine.request.get(context.requestId);
      if (!rq) return false;
      if (rq.userId !== actor.id && rq.teacherId !== actor.id) return false;
      if (![REQUEST_STATUS.DRAFT, REQUEST_STATUS.EDIT_REQUESTED].includes(rq.status)) return false;
    }

    if ((action === 'project.update') && role === 'teacher') {
      const pj = dataEngine.project.get(context.projectId);
      if (!pj) return false;
      const tp = dataEngine.teacher.get(pj.teacherId);
      if (!tp || tp.userId !== actor.id) return false;
    }

    return true;
  }

  assert(actor, action, context = {}) {
    if (!this.can(actor, action, context)) {
      const err = new Error(`forbidden:${action}`);
      err.code = 'FORBIDDEN';
      err.action = action;
      throw err;
    }
    return true;
  }
}

// ================================================================
// SECTION 6: DIFF ENGINE
// ================================================================

class DiffEngine {
  /**
   * دیف کامل بین دو نسخه
   * @returns {object} summary + جزئیات
   */
  compare(oldVer, newVer) {
    if (!oldVer || !newVer) {
      return { ok: false, errors: ['both_versions_required'] };
    }

    const result = {
      ok: true,
      oldVersionId: oldVer.id,
      newVersionId: newVer.id,
      chapters: { added: [], removed: [], modified: [], moved: [] },
      lessons:  { added: [], removed: [], modified: [], moved: [] },
      steps:    { added: [], removed: [], modified: [], moved: [] },
      videos:   { added: [], removed: [], modified: [], moved: [] },
      summary: {
        addedCount: 0, removedCount: 0, modifiedCount: 0, movedCount: 0,
        studentsAffected: 0, stepsPreserved: 0, stepsBroken: 0,
      },
    };

    const oldChaptersById = this._indexById(oldVer.chapters);
    const newChaptersById = this._indexById(newVer.chapters);

    // Chapters
    this._diffCollection({
      oldItems: oldVer.chapters,
      newItems: newVer.chapters,
      oldById: oldChaptersById,
      newById: newChaptersById,
      bucket: result.chapters,
      label: 'chapter',
    });

    // Lessons (flat)
    const oldLessons = this._flattenLessons(oldVer.chapters);
    const newLessons = this._flattenLessons(newVer.chapters);
    this._diffCollection({
      oldItems: oldLessons,
      newItems: newLessons,
      oldById: this._indexById(oldLessons),
      newById: this._indexById(newLessons),
      bucket: result.lessons,
      label: 'lesson',
      chapterAware: true,
    });

    // Steps (flat)
    const oldSteps = this._flattenSteps(oldVer.chapters);
    const newSteps = this._flattenSteps(newVer.chapters);
    this._diffCollection({
      oldItems: oldSteps,
      newItems: newSteps,
      oldById: this._indexById(oldSteps),
      newById: this._indexById(newSteps),
      bucket: result.steps,
      label: 'step',
      lessonAware: true,
    });

    // Videos (flat)
    const oldVideos = this._flattenVideos(oldVer.chapters);
    const newVideos = this._flattenVideos(newVer.chapters);
    this._diffCollection({
      oldItems: oldVideos,
      newItems: newVideos,
      oldById: this._indexById(oldVideos),
      newById: this._indexById(newVideos),
      bucket: result.videos,
      label: 'video',
      stepAware: true,
    });

    // محاسبه summary
    for (const k of ['chapters', 'lessons', 'steps', 'videos']) {
      result.summary.addedCount    += result[k].added.length;
      result.summary.removedCount  += result[k].removed.length;
      result.summary.modifiedCount += result[k].modified.length;
      result.summary.movedCount    += result[k].moved.length;
    }
    result.summary.stepsPreserved = newSteps.length - result.steps.added.length;
    result.summary.stepsBroken = result.steps.removed.length;

    return result;
  }

  _indexById(items) {
    const out = {};
    for (const it of (items || [])) if (it && it._id) out[it._id] = it;
    return out;
  }

  _flattenLessons(chapters) {
    const out = [];
    for (const ch of (chapters || [])) {
      for (const ls of (ch.lessons || [])) {
        out.push({ ...ls, _chapterId: ch._id, _chapterTitle: ch.title });
      }
    }
    return out;
  }

  _flattenSteps(chapters) {
    const out = [];
    for (const ch of (chapters || [])) {
      for (const ls of (ch.lessons || [])) {
        for (const st of (ls.steps || [])) {
          out.push({ ...st, _chapterId: ch._id, _lessonId: ls._id, _lessonTitle: ls.title });
        }
      }
    }
    return out;
  }

  _flattenVideos(chapters) {
    const out = [];
    for (const ch of (chapters || [])) {
      for (const ls of (ch.lessons || [])) {
        for (const st of (ls.steps || [])) {
          for (const v of (st.videos || [])) {
            out.push({ ...v, _chapterId: ch._id, _lessonId: ls._id, _stepId: st._id });
          }
        }
      }
    }
    return out;
  }

  _diffCollection({ oldItems, newItems, oldById, newById, bucket, label, chapterAware, lessonAware, stepAware }) {
    // Added
    for (const n of (newItems || [])) {
      if (!oldById[n._id]) {
        bucket.added.push(this._brief(n, label, { chapterAware, lessonAware, stepAware }));
      } else {
        const o = oldById[n._id];
        // Modified — مقایسه‌ی فیلدها
        const fieldsChanged = this._diffFields(o, n, label);
        if (fieldsChanged.length > 0) {
          bucket.modified.push(this._brief(n, label, { chapterAware, lessonAware, stepAware, fieldsChanged }));
        }
        // Moved — تغییر parent یا order
        if (chapterAware && o._chapterId !== n._chapterId) {
          bucket.moved.push(this._brief(n, label, { chapterAware, lessonAware, stepAware, fromChapterId: o._chapterId, toChapterId: n._chapterId }));
        } else if (lessonAware && o._lessonId !== n._lessonId) {
          bucket.moved.push(this._brief(n, label, { chapterAware, lessonAware, stepAware, fromLessonId: o._lessonId, toLessonId: n._lessonId }));
        } else if (stepAware && o._stepId !== n._stepId) {
          bucket.moved.push(this._brief(n, label, { chapterAware, lessonAware, stepAware, fromStepId: o._stepId, toStepId: n._stepId }));
        } else if (o.order !== n.order) {
          bucket.moved.push(this._brief(n, label, { chapterAware, lessonAware, stepAware, fromOrder: o.order, toOrder: n.order }));
        }
      }
    }
    // Removed
    for (const o of (oldItems || [])) {
      if (!newById[o._id]) {
        bucket.removed.push(this._brief(o, label, { chapterAware, lessonAware, stepAware }));
      }
    }
  }

  _brief(item, label, extra = {}) {
    const base = {
      _id: item._id,
      title: item.title || '',
      order: item.order,
    };
    if (label === 'chapter') base.title = item.title;
    if (label === 'lesson')  { base.title = item.title; base._chapterId = item._chapterId; }
    if (label === 'step')    { base.title = item.title; base._lessonId = item._lessonId; base._chapterId = item._chapterId; }
    if (label === 'video')   { base.title = item.title; base._stepId = item._stepId; base._lessonId = item._lessonId; }
    return { ...base, ...extra };
  }

  _diffFields(o, n, label) {
    const changed = [];
    if ((o.title || '') !== (n.title || '')) changed.push('title');
    if ((o.description || '') !== (n.description || '')) changed.push('description');
    if (label === 'step' && o.durationMinutes !== n.durationMinutes) changed.push('durationMinutes');
    if (label === 'lesson' && (o.imageBlobId || '') !== (n.imageBlobId || '')) changed.push('imageBlobId');
    if (label === 'video') {
      if ((o.blobId || '') !== (n.blobId || '')) changed.push('blobId');
      if ((o.externalUrl || '') !== (n.externalUrl || '')) changed.push('externalUrl');
      if ((o.title || '') !== (n.title || '')) changed.push('title');
    }
    return changed;
  }
}

// ================================================================
// SECTION 7: IMPACT ANALYZER
// ================================================================

class ImpactAnalyzer {
  /**
   * تحلیل تأثیر یک diff بر دانش‌آموزان
   * @param {string} projectId
   * @param {object} diff
   */
async analyze(projectId, diff) {
  if (!projectId || !diff || !diff.ok) {
    return { ok: false, errors: ['invalid_input'] };
  }

  const affectedStudents = await this._findAffectedStudents(projectId);

    const removedStepIds = diff.steps.removed.map((s) => s._id);
    const addedStepIds   = diff.steps.added.map((s) => s._id);
    const keptStepIds    = diff.steps.modified
      .map((s) => s._id)
      .concat(
        // stepهایی که دست نخوردن
        diff.steps.moved.map((s) => s._id)
      );

    // برای هر دانش‌آموز، بررسی تأثیر
    const perStudent = [];
    let impactedCompletedSteps = 0;
    let studentsLosingProgress = 0;
    let studentsGainingContent = 0;

  for (const sid of affectedStudents) {
    const progressKeys = await this._findStudentProgressKeys(sid, projectId);
      if (progressKeys.length === 0) continue;

      const studentImpact = {
        studentId: sid,
        progressKeys: progressKeys.length,
        removedCompletedSteps: [],
        keptCompletedSteps: [],
        totalCompletedBefore: 0,
        totalCompletedAfter: 0,
      };

      for (const pk of progressKeys) {
        const prog = engine.get(pk, null);
        if (!prog) continue;

        const completedIds = this._getCompletedStepIds(prog);
        studentImpact.totalCompletedBefore += completedIds.length;

        for (const stepId of completedIds) {
          if (removedStepIds.includes(stepId)) {
            studentImpact.removedCompletedSteps.push(stepId);
          } else {
            studentImpact.keptCompletedSteps.push(stepId);
          }
        }
        studentImpact.totalCompletedAfter = studentImpact.keptCompletedSteps.length;
      }

      if (studentImpact.removedCompletedSteps.length > 0) {
        studentsLosingProgress++;
        impactedCompletedSteps += studentImpact.removedCompletedSteps.length;
      }
      if (addedStepIds.length > 0 || keptStepIds.length > 0) {
        studentsGainingContent++;
      }

      perStudent.push(studentImpact);
    }

    return {
      ok: true,
      summary: {
        totalStudents: affectedStudents.length,
        studentsLosingProgress,
        studentsGainingContent,
        impactedCompletedSteps,
        removedStepCount: removedStepIds.length,
        addedStepCount: addedStepIds.length,
        keptStepCount: keptStepIds.length,
      },
      perStudent: perStudent.slice(0, 50), // فقط ۵۰ تای اول برای UI
      hasBreakingChanges: removedStepIds.length > 0,
    };
  }

async _findAffectedStudents(projectId) {
  const project = dataEngine.project.get(projectId);
  if (!project) return [];
  const grade = project.grade;
  const subject = project.subject;
  const folderName = project.folderName;

  const students = new Set();

  const allKeys = engine.keys('lesson-records-');
  for (const k of allKeys) {
    const userId = k.replace('lesson-records-', '');
    const records = engine.get(k, {}) || {};
    for (const r of Object.values(records)) {
      if (
        String(r.grade) === String(grade) &&
        r.subject === subject &&
        r.folderName === folderName
      ) {
        students.add(userId);
        break;
      }
    }
  }

  // 🆕 از سرور
  try {
    const allRecords = await serverQuery.getAllLessonRecords();
    for (const [userId, records] of Object.entries(allRecords)) {
      if (students.has(userId)) continue;
      for (const r of Object.values(records || {})) {
        if (
          String(r.grade) === String(grade) &&
          r.subject === subject &&
          r.folderName === folderName
        ) {
          students.add(userId);
          break;
        }
      }
    }
  } catch (e) {
    console.warn('[ImpactAnalyzer] server query failed:', e.message);
  }

  return Array.from(students);
}

async _findStudentProgressKeys(userId, projectId) {
  const project = dataEngine.project.get(projectId);
  if (!project) return [];
  const prefix = `lesson-progress-${userId}-${project.grade}-${project.subject}-${project.folderName}-`;

  const keys = engine.keys(prefix);
  if (keys.length > 0) return keys;

  try {
    const allProgress = await serverQuery.getAllLessonProgress();
    const userMap = allProgress[userId] || {};
    return Object.keys(userMap).filter((k) => k.startsWith(prefix));
  } catch {
    return [];
  }
}

  _getCompletedStepIds(prog) {
    if (!prog) return [];
    if (Array.isArray(prog.completedStepIds)) return prog.completedStepIds.slice();
    // legacy: completedSteps با index
    if (Array.isArray(prog.completedSteps)) return prog.completedSteps.map(String);
    return [];
  }
}

// ================================================================
// SECTION 8: PROGRESS MANAGER (Crown Jewel)
// ================================================================

class ProgressManager {
  /**
   * خواندن پیشرفت با فرمت یکپارچه
   * اگر legacy باشه، به‌صورت on-the-fly نرمالایز می‌کنه
   */
  read(userId, grade, subject, folderName, legacyLessonId, lessonContext = null) {
    if (!userId || userId === 'unknown') return this._empty();
    const key = this._key(userId, grade, subject, folderName, legacyLessonId);
    let raw = engine.get(key, null);

    if (!raw) return this._empty();

    // اگر schema جدید
    if (raw._v === PROGRESS_SCHEMA_VERSION && Array.isArray(raw.completedStepIds)) {
      return this._normalizeStored(raw);
    }

    // legacy: completedSteps با index
    if (Array.isArray(raw.completedSteps)) {
      const stepIds = this._legacyIndexesToStepIds(raw.completedSteps, lessonContext);
      return {
        completedStepIds: stepIds,
        completedSteps: raw.completedSteps.slice(),
        notes: raw.notes || {},
        lastVisitedAt: raw.lastVisitedAt || null,
        _v: PROGRESS_SCHEMA_VERSION,
        _migrated: false, // هنوز ذخیره نشده
        _legacyMigrated: true,
      };
    }

    return this._empty();
  }

  /**
   * نوشتن پیشرفت (فرمت جدید)
   */
  write(userId, grade, subject, folderName, legacyLessonId, progress) {
    if (!userId || userId === 'unknown') return { ok: false, error: 'no_user' };
    const key = this._key(userId, grade, subject, folderName, legacyLessonId);

    const toSave = {
      completedStepIds: Array.isArray(progress.completedStepIds) ? progress.completedStepIds.slice() : [],
      completedSteps: Array.isArray(progress.completedSteps) ? progress.completedSteps.slice() : [],
      notes: progress.notes && typeof progress.notes === 'object' ? { ...progress.notes } : {},
      lastVisitedAt: progress.lastVisitedAt || now(),
      _v: PROGRESS_SCHEMA_VERSION,
    };

    // 🆕 بدون silent — تا event storage:write صادر بشه و remoteSync فوراً push کنه
    const res = engine.set(key, toSave);
    if (res.ok) {
      engine.broadcastAndEmit('progress:changed', {
        userId, grade, subject, folderName, legacyLessonId,
      });
    }
    return res;
  }

  /**
   * افزودن step به completed با حفظ فرمت legacy
   */
  addCompletedStep(userId, grade, subject, folderName, legacyLessonId, stepId, stepIndex = null, lessonContext = null) {
    const cur = this.read(userId, grade, subject, folderName, legacyLessonId, lessonContext);
    if (!cur.completedStepIds.includes(stepId)) {
      cur.completedStepIds.push(stepId);
    }
    // legacy: index را هم اضافه کن
    if (stepIndex != null && !cur.completedSteps.includes(stepIndex)) {
      cur.completedSteps.push(stepIndex);
      cur.completedSteps.sort((a, b) => a - b);
    }
    cur.lastVisitedAt = now();
    return this.write(userId, grade, subject, folderName, legacyLessonId, cur);
  }

  removeCompletedStep(userId, grade, subject, folderName, legacyLessonId, stepId, lessonContext = null) {
    const cur = this.read(userId, grade, subject, folderName, legacyLessonId, lessonContext);
    cur.completedStepIds = cur.completedStepIds.filter((id) => id !== stepId);
    // legacy: index را هم حذف کن
    if (lessonContext) {
      const idx = lessonContext.steps?.findIndex((s) => s._id === stepId);
      if (idx != null && idx >= 0) {
        cur.completedSteps = cur.completedSteps.filter((i) => i !== idx);
      }
    }
    return this.write(userId, grade, subject, folderName, legacyLessonId, cur);
  }

  /**
   * ⭐ CROWN JEWEL — مهاجرت پیشرفت هنگام publish نسخه‌ی جدید
   * این تابع نباید هیچ‌وقت progress دانش‌آموز را کاملاً پاک کند
   */
  async migrateOnPublish({ projectId, oldVersion, newVersion, actor = null }) {
    if (!projectId || !oldVersion || !newVersion) {
      return { ok: false, errors: ['missing_params'] };
    }

    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, errors: ['project_not_found'] };

    // 🆕 مطالعه آزاد: هیچ progress migration لازم نیست
    if (project.type === 'freeStudy') {
      return {
        ok: true,
        summary: {
          skipped: true,
          reason: 'free_study',
          migratedStudents: 0,
          removedStepCount: 0,
          addedStepCount: 0,
          keptStepCount: 0,
          totalRemovedCompleted: 0,
          backupId: null,
        },
      };
    }

    const grade = project.grade;
    const subject = project.subject;
    const folderName = project.folderName;

    // ۱. پیدا کردن همه‌ی stepIds قدیمی و جدید
    const oldStepIds = this._collectStepIds(oldVersion.chapters);
    const newStepIds = this._collectStepIds(newVersion.chapters);

    const oldSet = new Set(oldStepIds);
    const newSet = new Set(newStepIds);

    const removedStepIds = oldStepIds.filter((id) => !newSet.has(id));
    const addedStepIds = newStepIds.filter((id) => !oldSet.has(id));
    const keptStepIds = newStepIds.filter((id) => oldSet.has(id));

    // ۲. snapshot قبل از migration (فقط برای rollback)
const backupId = `progress-backup-${projectId}-${now()}`;
const backupData = await this._snapshotProgress(projectId);
engine.set(backupId, {
      projectId,
      at: now(),
      data: backupData,
      counts: { students: Object.keys(backupData).length },
    }, { silent: true });
    this._rotateProgressBackups(projectId);

    // ۳. برای هر دانش‌آموز، migration
    const affectedStudentIds = Object.keys(backupData);
    let migratedCount = 0;
    let totalRemovedCompleted = 0;

    for (const sid of affectedStudentIds) {
      const studentProgressMap = backupData[sid];
      if (!studentProgressMap) continue;

      for (const [key, prog] of Object.entries(studentProgressMap)) {
        const newCompleted = [];
        const archivedIds = Array.isArray(prog.archivedStepIds) ? prog.archivedStepIds.slice() : [];
        const notesNew = { ...(prog.notes || {}) };
        const completedIds = this._getCompletedStepIds(prog);

        for (const stepId of completedIds) {
          if (removedStepIds.includes(stepId)) {
            // step حذف شده — آرشیو کن
            archivedIds.push(stepId);
            totalRemovedCompleted++;
          } else if (newSet.has(stepId)) {
            // step حفظ شده — پیشرفت باقی می‌مونه
            newCompleted.push(stepId);
          }
          // else: step ناشناخته (نباید رخ بده)
        }

        // ۴. legacy: completedSteps (index-based) را دوباره بساز
        // نکته: بعد از publish، legacy stepId ممکنه عوض بشه
        // پس از completedSteps قدیمی صرف‌نظر می‌کنیم و از _id استفاده می‌کنیم
        // ولی برای سازگاری با کد قدیمی، completedSteps را هم می‌سازیم
        const legacyCompletedSteps = this._stepIdsToLegacyIndexes(newCompleted, newVersion.chapters);

        const migrated = {
          completedStepIds: newCompleted,
          completedSteps: legacyCompletedSteps,
          archivedStepIds: archivedIds,
          notes: notesNew,
          lastVisitedAt: prog.lastVisitedAt || now(),
          _v: PROGRESS_SCHEMA_VERSION,
          _migrationHistory: [
            ...(Array.isArray(prog._migrationHistory) ? prog._migrationHistory : []),
            {
              fromVersionId: oldVersion.id,
              toVersionId: newVersion.id,
              at: now(),
              removedCount: completedIds.length - newCompleted.length,
            },
          ].slice(-10),
        };

        engine.set(key, migrated, { silent: true });
      }
      migratedCount++;
    }

    // ۵. ثبت در audit
    auditLog.record({
      actorId: actor?.id || null,
      actorRole: actor?.role || null,
      action: AUDIT_ACTION.PROGRESS_MIGRATE,
      targetType: 'project',
      targetId: projectId,
      before: { oldVersionId: oldVersion.id },
      after: { newVersionId: newVersion.id },
      note: `migrated ${migratedCount} students; removed ${removedStepIds.length} steps`,
    });

    return {
      ok: true,
      summary: {
        migratedStudents: migratedCount,
        removedStepCount: removedStepIds.length,
        addedStepCount: addedStepIds.length,
        keptStepCount: keptStepIds.length,
        totalRemovedCompleted,
        backupId,
      },
    };
  }

  /**
   * Rollback یک migration قبلی
   */
  rollbackMigration(backupId) {
    const bk = engine.get(backupId, null);
    if (!bk) return { ok: false, errors: ['backup_not_found'] };
    if (!bk.data) return { ok: false, errors: ['backup_empty'] };

    let restored = 0;
    for (const [sid, map] of Object.entries(bk.data)) {
      for (const [key, prog] of Object.entries(map)) {
        engine.set(key, prog, { silent: true });
        restored++;
      }
    }
    return { ok: true, restoredCount: restored, backupId };
  }

  /** لیست backupهای migration برای یک پروژه */
  listMigrationBackups(projectId) {
    const keys = engine.keys(`progress-backup-${projectId}-`);
    return keys
      .map((k) => {
        const data = engine.get(k, null);
        return data
          ? { key: k, at: data.at, counts: data.counts }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.at - a.at);
  }

  /** پاک‌سازی progress برای یک درس خاص (وقتی معلم درس را حذف می‌کند) */
  archiveLessonProgress(userId, grade, subject, folderName, legacyLessonId) {
    const key = this._key(userId, grade, subject, folderName, legacyLessonId);
    const raw = engine.get(key, null);
    if (!raw) return { ok: true, already: true };

    const orphanKey = `orphan-progress-${userId}-${folderName}-${legacyLessonId}`;
    engine.set(orphanKey, {
      originalKey: key,
      archivedAt: now(),
      data: raw,
      expiresAt: now() + RETENTION.ORPHAN_TTL_MS,
    }, { silent: true });
    engine.remove(key);
    return { ok: true, orphanKey };
  }

  // ---- private helpers ----

  _key(userId, grade, subject, folderName, lessonId) {
    return `lesson-progress-${userId}-${grade}-${subject}-${folderName}-${lessonId}`;
  }

  _empty() {
    return {
      completedStepIds: [],
      completedSteps: [],
      archivedStepIds: [],
      notes: {},
      lastVisitedAt: null,
      _v: PROGRESS_SCHEMA_VERSION,
    };
  }

  _normalizeStored(raw) {
    return {
      completedStepIds: Array.isArray(raw.completedStepIds) ? raw.completedStepIds.slice() : [],
      completedSteps: Array.isArray(raw.completedSteps) ? raw.completedSteps.slice() : [],
      archivedStepIds: Array.isArray(raw.archivedStepIds) ? raw.archivedStepIds.slice() : [],
      notes: raw.notes && typeof raw.notes === 'object' ? { ...raw.notes } : {},
      lastVisitedAt: raw.lastVisitedAt || null,
      _migrationHistory: Array.isArray(raw._migrationHistory) ? raw._migrationHistory.slice() : [],
      _v: PROGRESS_SCHEMA_VERSION,
    };
  }

  _legacyIndexesToStepIds(indexes, lessonContext) {
    if (!Array.isArray(indexes)) return [];
    if (!lessonContext || !Array.isArray(lessonContext.steps)) return [];
    const out = [];
    for (const idx of indexes) {
      const step = lessonContext.steps[idx];
      if (step && step._id) out.push(step._id);
    }
    return out;
  }

  _getCompletedStepIds(prog) {
    if (!prog) return [];
    if (Array.isArray(prog.completedStepIds)) return prog.completedStepIds.slice();
    if (Array.isArray(prog.completedSteps)) return prog.completedSteps.map(String);
    return [];
  }

  _stepIdsToLegacyIndexes(stepIds, chapters) {
    const allSteps = [];
    for (const ch of (chapters || [])) {
      for (const ls of (ch.lessons || [])) {
        for (const st of (ls.steps || [])) allSteps.push(st);
      }
    }
    const indexes = [];
    stepIds.forEach((id) => {
      const idx = allSteps.findIndex((s) => s._id === id);
      if (idx >= 0) indexes.push(idx);
    });
    return indexes.sort((a, b) => a - b);
  }

  _collectStepIds(chapters) {
    const out = [];
    for (const ch of (chapters || [])) {
      for (const ls of (ch.lessons || [])) {
        for (const st of (ls.steps || [])) if (st._id) out.push(st._id);
      }
    }
    return out;
  }

async _snapshotProgress(projectId) {
  const project = dataEngine.project.get(projectId);
  if (!project) return {};
  const snapshot = {};

  const allKeys = engine.keys('lesson-progress-');
  for (const k of allKeys) {
    if (!k.includes(`-${project.grade}-${project.subject}-${project.folderName}-`)) continue;
    const parts = k.slice('lesson-progress-'.length).split('-');
    if (parts.length < 5) continue;
    const userId = parts[0];
    if (!snapshot[userId]) snapshot[userId] = {};
    snapshot[userId][k] = engine.get(k, null);
  }

  // 🆕 از سرور
  try {
    const allProgress = await serverQuery.getAllLessonProgress();
    for (const [userId, map] of Object.entries(allProgress)) {
      for (const [k, v] of Object.entries(map)) {
        if (!k.includes(`-${project.grade}-${project.subject}-${project.folderName}-`)) continue;
        if (!snapshot[userId]) snapshot[userId] = {};
        if (!snapshot[userId][k]) snapshot[userId][k] = v;
      }
    }
  } catch (e) {
    console.warn('[ProgressManager] server progress fetch failed:', e.message);
  }

  return snapshot;
}

    // ================================================================
  // 🆕 QUIZ STEP PASS MANAGEMENT
  // ================================================================

  /** ثبت قبولی دانش‌آموز در یک آزمون مرحله */
  // ================================================================
  // 🆕 UNIFIED STEP-QUIZ PROGRESS (Single Source of Truth)
  // ================================================================
  // کلید: step-quiz-progress-{userId}-{projectId}
  // ساختار: { [stepId]: { attempts, passed, bestScore, unlockedWithPayment, ... } }
  // چرا {projectId} نه {quizId}؟ چون quizId با clone عوض می‌شود ولی projectId پایدار است.
  // ================================================================

  _quizProgressKey(userId, projectId) {
    return `${KEYS.STEP_QUIZ_PROGRESS_PREFIX}${userId}-${projectId}`;
  }

  /** خواندن رکورد کامل یک step (یا کل پروژه اگر stepId=null) */
  getQuizStepProgress(userId, projectId, stepId = null) {
    if (!userId || userId === 'unknown' || !projectId) {
      return stepId ? null : {};
    }
    const key = this._quizProgressKey(userId, projectId);
    const data = engine.get(key, {}) || {};
    return stepId ? (data[stepId] || null) : data;
  }

  /** همه‌ی رکوردهای یک پروژه */
  getAllQuizProgressForProject(userId, projectId) {
    return this.getQuizStepProgress(userId, projectId, null);
  }

  /**
   * 🎯 تابع مرکزی: ثبت یک تلاش (موفق یا ناموفق)
   * @param {Object} opts - { quizId, score, passed, passThreshold }
   * @returns {{ ok, data, isFirstPass }}
   */
  recordQuizAttempt(userId, projectId, stepId, opts = {}) {
    if (!userId || userId === 'unknown' || !projectId || !stepId) {
      return { ok: false, error: 'invalid_args' };
    }

    const { quizId = null, score = 0, passed = false, passThreshold = 12 } = opts;
    const key = this._quizProgressKey(userId, projectId);
    const all = engine.get(key, {}) || {};

    const prev = all[stepId] || {
      attempts: [],
      passed: false,
      bestScore: 0,
      firstPassedAt: null,
      lastAttemptAt: null,
      unlockedWithPayment: [],
    };

    const wasPassed = !!prev.passed;
    const attemptNumber = (prev.attempts?.length || 0) + 1;
    const numScore = Number(score) || 0;

    const newAttempt = {
      attemptNumber,
      quizId: quizId || null,
      score: numScore,
      passed: !!passed,
      passThreshold: Number(passThreshold) || 12,
      submittedAt: Date.now(),
    };

    const next = {
      ...prev,
      attempts: [...(prev.attempts || []), newAttempt],
      passed: wasPassed || !!passed,
      bestScore: Math.max(prev.bestScore || 0, numScore),
      firstPassedAt: (!wasPassed && passed) ? Date.now() : prev.firstPassedAt,
      lastAttemptAt: Date.now(),
    };

    all[stepId] = next;
    // 🆕 بدون silent — تا remoteSync فوراً push کنه
    const res = engine.set(key, all);

    if (res.ok) {
      engine.broadcastAndEmit('quiz:step-progress-changed', {
        userId, projectId, stepId,
        passed: next.passed,
        attemptNumber,
        isFirstPass: !wasPassed && passed,
      });

      try {
        auditLog.record({
          actorId: userId,
          actorRole: 'student',
          action: passed ? 'quiz.step_passed' : 'quiz.step_failed',
          targetType: 'step',
          targetId: stepId,
          after: { quizId, score: numScore, attemptNumber, projectId },
        });
      } catch { /* audit failure shouldn't break flow */ }

      // backward compat
      if (passed) {
        engine.broadcastAndEmit('quiz:step-passed', {
          userId, projectId, stepId, quizId, score: numScore,
        });
      }
    }

    return { ok: res.ok, data: next, isFirstPass: !wasPassed && passed };
  }

  /** API سازگاری با کد قدیمی — به recordQuizAttempt delegate می‌شود */
  recordQuizPass(userId, projectId, stepId, { quizId, score } = {}) {
    return this.recordQuizAttempt(userId, projectId, stepId, {
      quizId,
      score,
      passed: true,
      passThreshold: 0,
    });
  }

  /** آیا دانش‌آموز در این مرحله قبول شده؟ */
  isQuizStepPassed(userId, projectId, stepId) {
    const rec = this.getQuizStepProgress(userId, projectId, stepId);
    return !!rec?.passed;
  }

  /** اطلاعات کامل قبولی (backward compat با ساختار قبلی) */
  getQuizStepPass(userId, projectId, stepId) {
    const rec = this.getQuizStepProgress(userId, projectId, stepId);
    if (!rec || !rec.passed) return null;
    const firstPass = rec.attempts.find((a) => a.passed) || rec.attempts[rec.attempts.length - 1];
    return {
      quizId: firstPass?.quizId || null,
      score: rec.bestScore,
      passedAt: rec.firstPassedAt,
      attemptsCount: rec.attempts.length,
    };
  }

  /** گرفتن همه‌ی pass های یک دانش‌آموز در یک پروژه */
  getAllQuizPassesForProject(userId, projectId) {
    const all = this.getAllQuizProgressForProject(userId, projectId);
    const out = {};
    for (const [stepId, rec] of Object.entries(all)) {
      if (rec?.passed) out[stepId] = this.getQuizStepPass(userId, projectId, stepId);
    }
    return out;
  }

  /** تعداد تلاش‌های انجام‌شده */
  getQuizAttemptsCount(userId, projectId, stepId) {
    const rec = this.getQuizStepProgress(userId, projectId, stepId);
    return rec?.attempts?.length || 0;
  }

    // ================================================================
  // 🆕 چک کامل بودن یک درس (همه‌ی steps)
  // ================================================================
  isLessonComplete(userId, projectId, grade, subject, folderName, lesson) {
    if (!userId || userId === 'unknown' || !lesson) return false;
    const legacyLessonId = lesson.legacyLessonId;
    if (!legacyLessonId) return false;

    const steps = lesson.steps || [];
    if (steps.length === 0) return false;

    const progress = this.read(userId, grade, subject, folderName, legacyLessonId, lesson);
    const completedIds = progress.completedStepIds || [];

    for (const step of steps) {
      if (step.type === 'quiz') {
        const p = this.getQuizStepProgress(userId, projectId, step._id);
        if (!p?.passed) return false;
      } else {
        if (!completedIds.includes(step._id)) return false;
      }
    }
    return true;
  }

  // ================================================================
  // 🆕 گرفتن وضعیت قفل همه‌ی فصل‌ها و درس‌ها
  // ================================================================
  getBookLocks(userId, projectId, grade, subject, folderName, chapters) {
    const result = {
      chapterComplete: [],
      lessonComplete: [],
    };
    if (!Array.isArray(chapters)) return result;

    for (let ci = 0; ci < chapters.length; ci++) {
      const ch = chapters[ci];
      const lessons = ch.lessons || [];
      const lsDone = [];

      for (let li = 0; li < lessons.length; li++) {
        const done = this.isLessonComplete(
          userId, projectId, grade, subject, folderName, lessons[li]
        );
        lsDone.push(done);
      }

      result.lessonComplete.push(lsDone);
      result.chapterComplete.push(lsDone.length > 0 && lsDone.every(Boolean));
    }
    return result;
  }

  /**
   * 🎯 تابع مرکزی: بررسی مجاز بودن شرکت
   * @returns {{ allowed, reason, attemptsLeft, totalAttempts, retryPrice }}
   */
  canAttemptQuiz(userId, projectId, stepId, opts = {}) {
    const { maxAttempts = 2, retryPrice = 0 } = opts;
    const rec = this.getQuizStepProgress(userId, projectId, stepId);
    const attemptsCount = rec?.attempts?.length || 0;
    const passed = !!rec?.passed;
    const unlockedCount = rec?.unlockedWithPayment?.length || 0;

    // 🆕 هر پرداخت = ۱ تلاش اضافه (نه maxAttempts)
    const totalAllowed = maxAttempts + unlockedCount;

    if (passed) {
      return {
        allowed: false,
        reason: 'already-passed',
        attemptsLeft: 0,
        totalAttempts: attemptsCount,
        totalAllowed,
        retryPrice,
      };
    }

    if (attemptsCount < totalAllowed) {
      const isFree = attemptsCount < maxAttempts;
      return {
        allowed: true,
        reason: isFree ? 'free-attempt' : 'paid-attempt',
        attemptsLeft: totalAllowed - attemptsCount,
        totalAttempts: attemptsCount,
        totalAllowed,
        retryPrice,
      };
    }

    return {
      allowed: false,
      reason: 'payment-required',
      attemptsLeft: 0,
      totalAttempts: attemptsCount,
      totalAllowed,
      retryPrice,
    };
  }

  /** ثبت پرداخت و باز کردن تلاش‌های اضافه */
  unlockQuizWithPayment(userId, projectId, stepId, opts = {}) {
    const { amount = 0, txId = null, gateway = 'mock' } = opts;
    if (!userId || !projectId || !stepId) return { ok: false, error: 'invalid_args' };

    const key = this._quizProgressKey(userId, projectId);
    const all = engine.get(key, {}) || {};
    const rec = all[stepId] || {
      attempts: [], passed: false, bestScore: 0,
      firstPassedAt: null, lastAttemptAt: null, unlockedWithPayment: [],
    };

    rec.unlockedWithPayment = [
      ...(rec.unlockedWithPayment || []),
      {
        amount: Number(amount) || 0,
        txId: txId || `sim_${Date.now()}`,
        gateway,
        at: Date.now(),
      },
    ];

    all[stepId] = rec;
    // 🆕 بدون silent — تا remoteSync فوراً push کنه
    const res = engine.set(key, all);

    if (res.ok) {
      engine.broadcastAndEmit('quiz:unlocked', { userId, projectId, stepId, amount, txId });
      try {
        auditLog.record({
          actorId: userId, actorRole: 'student',
          action: 'quiz.step_unlocked_with_payment',
          targetType: 'step', targetId: stepId,
          after: { amount, gateway, projectId, txId },
        });
      } catch { /* ignore */ }
    }
    return res;
  }


  _rotateProgressBackups(projectId) {
    const keys = engine.keys(`progress-backup-${projectId}-`);
    const sorted = keys
      .map((k) => ({ k, at: engine.get(k, {})?.at || 0 }))
      .sort((a, b) => b.at - a.at);
    for (let i = RETENTION.PROGRESS_BACKUP_KEEP; i < sorted.length; i++) {
      engine.remove(sorted[i].k);
    }
  }
}








// ================================================================
// SECTION 8.5: SUBSCRIPTION MANAGER 🆕
// ================================================================
// مدیریت اشتراک دانش‌آموزان — یک منبع واحد حقیقت
// ساختار:
//   - subscription-{userId}-{projectId}  ← رکورد اصلی
//   - subscriptions-user-{userId}        ← ایندکس کاربر
//   - subscriptions-project-{projectId}  ← ایندکس پروژه
//   - subscription-tx-{txId}             ← idempotency
// ================================================================

const SUBSCRIPTION_SCHEMA = '1.0';

class SubscriptionManager {
  constructor() {
    this._saltCache = null;
  }

  // ─── Salt برای امضا ───
_getSalt() {
  if (this._saltCache) return this._saltCache;
  let salt = engine.get(KEYS.SUBSCRIPTION_SALT, null);
  if (!salt) {
    salt = `sub_local_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
    engine.set(KEYS.SUBSCRIPTION_SALT, salt, { silent: true });
    console.warn('[Subscription] salt not synced yet, using local');
  }
  this._saltCache = salt;
  return salt;
}

// 🆕 برای صدا زدن بعد از pull از سرور
invalidateSaltCache() {
  this._saltCache = null;
}

  // ─── امضای رکورد ───
  _computeSignature(record) {
    const salt = this._getSalt();
    const payload = [
      record.userId, record.projectId, record.teacherId,
      record.amount, record.issuedAt, record.expiresAt, record.status,
    ].join('|');
    const key = `${salt}::${record.userId}::${record.projectId}`;

    // FNV-1a hash ساده و سریع
    let h1 = 2166136261;
    let h2 = 2166136261 ^ 0x5bd1e995;
    const str = `${payload}::${key}`;
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      h1 ^= c; h1 = Math.imul(h1, 16777619);
      h2 ^= (c * 2654435761) & 0xff; h2 = Math.imul(h2, 16777619);
    }
    return `s_${(h1 >>> 0).toString(16)}_${(h2 >>> 0).toString(16)}`;
  }

  _sign(record) {
    return { ...record, _sig: this._computeSignature(record) };
  }

  _verify(record) {
    if (!record || !record._sig) return false;
    return record._sig === this._computeSignature(record);
  }

  // ─── کلیدها ───
  _mainKey(userId, projectId) {
    return `${KEYS.SUBSCRIPTION_PREFIX}${userId}-${projectId}`;
  }
  _userKey(userId) {
    return `${KEYS.SUBSCRIPTION_BY_USER_PREFIX}${userId}`;
  }
  _projectKey(projectId) {
    return `${KEYS.SUBSCRIPTION_BY_PROJECT_PREFIX}${projectId}`;
  }
  _txKey(txId) {
    return `${KEYS.SUBSCRIPTION_TX_PREFIX}${txId}`;
  }

  // ─── Idempotency ───
  hasTxId(txId) {
    if (!txId) return false;
    return !!engine.get(this._txKey(txId), null);
  }
  _recordTx(txId, meta) {
    if (!txId) return;
    engine.set(this._txKey(txId), { ...meta, at: Date.now() }, { silent: true });
  }

  // ─── خواندن ───
  get(userId, projectId) {
    if (!userId || !projectId) return null;
    const raw = engine.get(this._mainKey(userId, projectId), null);
    if (!raw) return null;
    if (!this._verify(raw)) {
      console.warn('[Subscription] invalid signature, ignoring record');
      return null;
    }
    return raw;
  }

  getStatus(userId, projectId) {
    const record = this.get(userId, projectId);
    if (!record) {
      return { status: 'none', daysLeft: 0, expiresAt: null, record: null };
    }
    const now = Date.now();
    // چک ساعت معکوس
    if (now < record.issuedAt - 60 * 1000) {
      return { status: 'invalid', daysLeft: 0, expiresAt: record.expiresAt, record };
    }
    // چک refund
    if (record.status === 'refunded') {
      return { status: 'refunded', daysLeft: 0, expiresAt: record.expiresAt, record };
    }
    // چک انقضا
    if (now >= record.expiresAt) {
      return { status: 'expired', daysLeft: 0, expiresAt: record.expiresAt, record };
    }
    const daysLeft = Math.ceil((record.expiresAt - now) / 86400000);
    return {
      status: 'active',
      daysLeft,
      expiresAt: record.expiresAt,
      needsRenewal: daysLeft <= 30,
      record,
    };
  }

  // ─── تصمیم‌گیری دسترسی ───
  checkAccess({ user, project, lesson, userId }) {
    // ۱. ادمین
    if (user?.role === 'admin') {
      return { allowed: true, reason: 'admin' };
    }
    // ۲. مالک پروژه (خود معلم)
    if (user?.role === 'teacher' && user.id === (project?.teacherUserId || project?.userId)) {
      return { allowed: true, reason: 'owner' };
    }
    // همچنین اگر teacherProfile.userId === user.id و teacherId === project.teacherId
    if (user?.role === 'teacher' && project?.teacherUserId && user.id === project.teacherUserId) {
      return { allowed: true, reason: 'owner' };
    }
    // ۳. سیستم اشتراک غیرفعال
    if (!project || !project.subscriptionEnabled || (Number(project.subscriptionPrice) || 0) <= 0) {
      return { allowed: true, reason: 'free-project' };
    }
    // ۴. درس رایگان
    if (lesson?.isFree === true) {
      return { allowed: true, reason: 'free-lesson' };
    }
    // ۵-۸. بر اساس اشتراک
    const status = this.getStatus(userId, project.id);
    if (status.status === 'refunded') {
      return { allowed: false, reason: 'refunded' };
    }
    if (status.status === 'expired') {
      return { allowed: false, reason: 'expired' };
    }
    if (status.status === 'active') {
      return { allowed: true, reason: 'subscribed', daysLeft: status.daysLeft };
    }
    // ۹. هیچ اشتراکی ندارد
    return { allowed: false, reason: 'not-subscribed' };
  }

  // ─── ایندکس‌ها ───
  _updateUserIndex(userId, projectId, action = 'add') {
    const key = this._userKey(userId);
    const list = engine.get(key, []) || [];
    if (action === 'add') {
      if (!list.includes(projectId)) {
        engine.set(key, [...list, projectId], { silent: true });
      }
    } else if (action === 'remove') {
      engine.set(key, list.filter((p) => p !== projectId), { silent: true });
    }
  }

  _updateProjectIndex(projectId, userId, meta = null, action = 'add') {
    const key = this._projectKey(projectId);
    const list = engine.get(key, []) || [];
    if (action === 'add') {
      const filtered = list.filter((x) => x.userId !== userId);
      engine.set(key, [...filtered, { userId, ...meta }], { silent: true });
    } else if (action === 'remove') {
      engine.set(key, list.filter((x) => x.userId !== userId), { silent: true });
    }
  }

  // ─── خرید اول ───
  purchase({ userId, projectId, teacherId, amount, txId, gateway = 'mock', durationDays = 365 }) {
    if (!userId || !projectId) return { ok: false, error: 'invalid_args' };
    if (this.hasTxId(txId)) return { ok: false, error: 'duplicate_tx' };

    const existing = this.get(userId, projectId);
    if (existing && existing.status === 'active' && Date.now() < existing.expiresAt) {
      return { ok: false, error: 'already_subscribed' };
    }

    const now = Date.now();
    const expiresAt = now + durationDays * 86400000;

    let record = {
      _v: SUBSCRIPTION_SCHEMA,
      userId, projectId, teacherId: teacherId || null,
      amount: Number(amount) || 0,
      purchasedAt: now,
      issuedAt: now,
      expiresAt,
      status: 'active',
      history: [{
        txId: txId || `sim_${now}`,
        amount: Number(amount) || 0,
        type: 'initial',
        at: now,
        gateway,
      }],
    };
    record = this._sign(record);

    const key = this._mainKey(userId, projectId);
    // 🆕 بدون silent — تا remoteSync فوراً push کنه
    const res = engine.set(key, record);
    if (!res.ok) return { ok: false, error: 'save_failed' };

    this._recordTx(txId, { userId, projectId, type: 'initial' });
    this._updateUserIndex(userId, projectId, 'add');
    this._updateProjectIndex(projectId, userId, {
      expiresAt, status: 'active', amount: record.amount,
    }, 'add');

    engine.broadcastAndEmit('subscription:changed', { userId, projectId });
    try {
      auditLog.record({
        actorId: userId, actorRole: 'student',
        action: 'subscription.purchase',
        targetType: 'project', targetId: projectId,
        after: { amount, txId, expiresAt },
      });
    } catch {}

    return { ok: true, record };
  }

  // ─── تمدید ───
  renew({ userId, projectId, amount, txId, gateway = 'mock', durationDays = 365 }) {
    if (!userId || !projectId) return { ok: false, error: 'invalid_args' };
    if (this.hasTxId(txId)) return { ok: false, error: 'duplicate_tx' };

    const existing = this.get(userId, projectId);
    const now = Date.now();

    // اگر اشتراک فعال دارد → افزودن به انقضای موجود
    // اگر منقضی است → شروع از حالا
    let baseTime = now;
    if (existing && existing.status === 'active' && existing.expiresAt > now) {
      baseTime = existing.expiresAt;
    }

    const newExpiresAt = baseTime + durationDays * 86400000;

    const record = this._sign({
      _v: SUBSCRIPTION_SCHEMA,
      userId, projectId,
      teacherId: existing?.teacherId || null,
      amount: Number(amount) || 0,
      purchasedAt: existing?.purchasedAt || now,
      issuedAt: now,
      expiresAt: newExpiresAt,
      status: 'active',
      history: [
        ...(existing?.history || []),
        {
          txId: txId || `sim_${now}`,
          amount: Number(amount) || 0,
          type: 'renewal',
          at: now,
          gateway,
        },
      ],
    });

    const key = this._mainKey(userId, projectId);
    // 🆕 بدون silent — تا remoteSync فوراً push کنه
    const res = engine.set(key, record);
    if (!res.ok) return { ok: false, error: 'save_failed' };

    this._recordTx(txId, { userId, projectId, type: 'renewal' });
    this._updateUserIndex(userId, projectId, 'add');
    this._updateProjectIndex(projectId, userId, {
      expiresAt: newExpiresAt, status: 'active', amount: record.amount,
    }, 'add');

    engine.broadcastAndEmit('subscription:changed', { userId, projectId });
    return { ok: true, record };
  }

  // ─── لغو (refund) ───
  refund({ userId, projectId, reason = '', actorId = null }) {
    const existing = this.get(userId, projectId);
    if (!existing) return { ok: false, error: 'not_found' };

    const record = this._sign({
      ...existing,
      status: 'refunded',
      expiresAt: Math.min(existing.expiresAt, Date.now()),
      history: [
        ...(existing.history || []),
        { type: 'refund', at: Date.now(), reason, actorId },
      ],
    });

    // 🆕 بدون silent — تا remoteSync فوراً push کنه
    const res = engine.set(this._mainKey(userId, projectId), record);
    if (res.ok) {
      this._updateProjectIndex(projectId, userId, { status: 'refunded' }, 'add');
      engine.broadcastAndEmit('subscription:changed', { userId, projectId });
      try {
        auditLog.record({
          actorId, actorRole: 'admin',
          action: 'subscription.refund',
          targetType: 'project', targetId: projectId,
          after: { userId, reason },
        });
      } catch {}
    }
    return { ok: res.ok, record };
  }

  // ─── لغو گروهی هنگام حذف پروژه ───
  refundAllForProject(projectId, { reason = 'project_deleted', actorId = null } = {}) {
    const list = this.listByProject(projectId);
    let count = 0;
    for (const sub of list) {
      if (sub.status === 'active' || sub.status === 'expired') {
        this.refund({
          userId: sub.userId, projectId, reason, actorId,
        });
        count++;
      }
    }
    return { ok: true, count };
  }

  // ─── Query ───
  listByUser(userId) {
    const projectIds = engine.get(this._userKey(userId), []) || [];
    return projectIds
      .map((pid) => this.get(userId, pid))
      .filter(Boolean);
  }

  listByProject(projectId) {
    const list = engine.get(this._projectKey(projectId), []) || [];
    return list.map(({ userId }) => ({
      userId,
      ...this.getStatus(userId, projectId),
    }));
  }

  // ─── Lazy expiry ───
  _lastExpireCheck = 0;
  expireStale() {
    const now = Date.now();
    if (now - this._lastExpireCheck < 5 * 60 * 1000) return { skipped: true };
    this._lastExpireCheck = now;

    // این تابع می‌تواند در آینده با سرور همگام شود
    // فعلاً هیچ کاری لازم نیست چون getStatus هر بار چک می‌کند
    return { ok: true };
  }
}

export const subscriptionManager = new SubscriptionManager();











// ================================================================
// SECTION 9: ORPHAN ARCHIVE
// ================================================================

class OrphanArchive {
  archiveProject(projectId, { actorId = null } = {}) {
    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, errors: ['not_found'] };

    const key = `orphan-project-${projectId}`;
    engine.set(key, {
      projectId,
      archivedAt: now(),
      expiresAt: now() + RETENTION.ORPHAN_TTL_MS,
      snapshot: JSON.parse(JSON.stringify(project)),
      archivedBy: actorId,
    }, { silent: true });
    return { ok: true, orphanKey: key };
  }

  restoreProject(projectId) {
    const key = `orphan-project-${projectId}`;
    const rec = engine.get(key, null);
    if (!rec) return { ok: false, errors: ['orphan_not_found'] };
    const res = dataEngine.project.restore(projectId);
    if (res.ok) engine.remove(key);
    return res;
  }

  listOrphans() {
    const keys = engine.keys('orphan-project-');
    return keys.map((k) => {
      const r = engine.get(k, null);
      return r ? { key: k, ...r } : null;
    }).filter(Boolean);
  }

  purgeExpired() {
    const all = this.listOrphans();
    const cutoff = now();
    let removed = 0;
    for (const o of all) {
      if (o.expiresAt && o.expiresAt < cutoff) {
        engine.remove(o.key);
        removed++;
      }
    }
    return { ok: true, removed };
  }
}

// ================================================================
// SECTION 10: VERSION MANAGER (High-Level)
// ================================================================

class VersionManager {
  /**
   * ساخت یک draft جدید از روی published موجود
   */
  createDraftFromPublished(projectId, { actor } = {}) {
    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, errors: ['project_not_found'] };
    if (!project.publishedVersionId) return { ok: false, errors: ['no_published_version'] };

    // اگر draft موجود است، برگردان
    if (project.draftVersionId) {
      const existing = dataEngine.version.get(project.draftVersionId);
      if (existing) return { ok: true, data: existing, existing: true };
    }

    const pub = dataEngine.version.get(project.publishedVersionId);
    if (!pub) return { ok: false, errors: ['published_not_found'] };

    const draft = dataEngine.version.create({
      projectId,
      parentVersionId: pub.id,
      status: VERSION_STATUS.DRAFT,
      chapters: JSON.parse(JSON.stringify(pub.chapters)).map(createChapter),
      createdBy: actor?.id || null,
    });
    if (!draft.ok) return draft;

    dataEngine.project.setDraft(projectId, draft.data.id);

    try {
      engine.emit('version:draft-created-from-published', {
        projectId,
        draftVersionId: draft.data.id,
        sourceVersionId: pub.id,
        actorId: actor?.id || null,
      });
    } catch (e) { /* listener failure shouldn't break the flow */ }

    
    return draft;
  }

  /**
   * ساخت draft اول (بدون published)
   */
  createInitialDraft(projectId, { actor } = {}) {
    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, errors: ['project_not_found'] };
    if (project.publishedVersionId) return this.createDraftFromPublished(projectId, { actor });

    if (project.draftVersionId) {
      const existing = dataEngine.version.get(project.draftVersionId);
      if (existing) return { ok: true, data: existing, existing: true };
    }

    const draft = dataEngine.version.create({
      projectId,
      parentVersionId: null,
      status: VERSION_STATUS.DRAFT,
      chapters: [],
      createdBy: actor?.id || null,
    });
    if (!draft.ok) return draft;

    dataEngine.project.setDraft(projectId, draft.data.id);
    return draft;
  }

  /**
   * ارسال draft برای بررسی (initial یا update)
   */
  submitForReview(projectId, { actor, changelog = [], note = '' } = {}) {
    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, errors: ['project_not_found'] };
    if (!project.draftVersionId) return { ok: false, errors: ['no_draft'] };

    const draft = dataEngine.version.get(project.draftVersionId);
    if (!draft) return { ok: false, errors: ['draft_not_found'] };

    // 🆕 اعتبارسنجی بر اساس نوع پروژه
    if (project.type === 'freeStudy') {
      if (!Array.isArray(draft.videos) || draft.videos.length === 0) {
        return { ok: false, errors: ['no_videos'] };
      }
      if (draft.videos.every((v) => !v.blobId && !v.externalUrl)) {
        return { ok: false, errors: ['videos_missing_source'] };
      }
      if (!draft.videos.every((v) => v.title && v.title.trim())) {
        return { ok: false, errors: ['videos_missing_title'] };
      }
    } else {
      if (!Array.isArray(draft.chapters) || draft.chapters.length === 0) {
        return { ok: false, errors: ['no_chapters'] };
      }
    }

    // تغییر وضعیت draft به pending
    dataEngine.version.markPending(draft.id);
    
    // 🆕 اصلاح باگ تداخل پیام‌ها و پاکسازی پیام‌های قبلی هنگام ارسال جدید
    const updatePayload = {
      adminNotes: null,       // پاک کردن پیام قبلی مدیر
      rejectionReason: null,  // پاک کردن دلیل رد قبلی
    };
    if (note) updatePayload.teacherNote = note; // ذخیره یادداشت معلم در فیلد مجزا
    if (changelog.length > 0) updatePayload.changelog = [...draft.changelog, ...changelog];

    dataEngine.version.update(draft.id, updatePayload);

    // ثبت pending روی project
    dataEngine.project.setPending(projectId, draft.id);

    // 🆕 تشخیص درست: اگر پروژه هرگز منتشر نشده باشد = اولیه، وگرنه = بروزرسانی
    const hasBeenPublished =
      !!project.publishedVersionId ||
      project.state === PROJECT_STATE.PUBLISHED ||
      project.state === PROJECT_STATE.UPDATE_PENDING ||
      project.state === PROJECT_STATE.SUSPENDED;

    const isInitial = !hasBeenPublished;
    const targetState = isInitial
      ? PROJECT_STATE.PENDING_INITIAL
      : PROJECT_STATE.UPDATE_PENDING;

    // 🆕 تلاش برای transition با چک نتیجه
    let transitionRes = null;
    try {
      transitionRes = dataEngine.project.transitionState(projectId, targetState, {
        byUserId: actor?.id,
      });
    } catch (e) {
      transitionRes = { ok: false, errors: [String(e)] };
    }

    // 🆕 اگر transition موفق نشد، به‌صورت مستقیم state را ست کن
    if (!transitionRes?.ok) {
      console.warn('[submitForReview] transition failed, force-setting state:', transitionRes?.errors);
      try {
        const allProjects = engine.get(KEYS.PROJECTS, {}) || {};
        const pj = allProjects[projectId];
        if (pj) {
          allProjects[projectId] = {
            ...pj,
            state: targetState,
            pendingVersionId: draft.id,
            updatedAt: Date.now(),
          };
          engine.set(KEYS.PROJECTS, allProjects, { silent: true });
          engine.broadcastAndEmit('registry:project:changed', { key: KEYS.PROJECTS });
        }
      } catch (e) {
        console.error('[submitForReview] force-set failed:', e);
      }
    }

    // audit
    auditLog.record({
      actorId: actor?.id, actorRole: actor?.role,
      action: AUDIT_ACTION.VERSION_SUBMIT,
      targetType: 'project', targetId: projectId,
      after: { versionId: draft.id, kind: isInitial ? 'initial' : 'update' },
    });

    // notify admins
        this._notifyAdmins(NOTIF_TYPE.VERSION_SUBMITTED, {
      projectId,
      versionId: draft.id,
      teacherName: this._teacherName(project.teacherId),
      subject: project.subject,
      grade: project.grade,
      field: project.field || null,
      kind: isInitial ? 'initial' : 'update',
    });

    return { ok: true, data: { versionId: draft.id, isInitial } };
  }

  /**
   * تایید و انتشار نسخه‌ی pending
   */
  approveAndPublish(projectId, { actor, note = '' } = {}) {
    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, errors: ['project_not_found'] };
    if (!project.pendingVersionId) return { ok: false, errors: ['no_pending_version'] };

    const pending = dataEngine.version.get(project.pendingVersionId);
    if (!pending) return { ok: false, errors: ['pending_not_found'] };

    const oldPublishedId = project.publishedVersionId;
    const oldVersion = oldPublishedId ? dataEngine.version.get(oldPublishedId) : null;

    // ۱. اگر نسخه‌ی قبلی وجود دارد، قبل از publish migration انجام بده
    let migrationResult = null;
    if (oldVersion) {
      migrationResult = progressManager.migrateOnPublish({
        projectId,
        oldVersion,
        newVersion: pending,
        actor,
      });
    }

    // ۲. Publish در dataEngine
    const pubRes = dataEngine.project.publish(projectId, pending.id, { byUserId: actor?.id });
    if (!pubRes.ok) return pubRes;

    // ۳. Audit
    auditLog.record({
      actorId: actor?.id, actorRole: actor?.role,
      action: AUDIT_ACTION.VERSION_APPROVE,
      targetType: 'project', targetId: projectId,
      before: { publishedVersionId: oldPublishedId },
      after: { publishedVersionId: pending.id, migrationResult },
      note,
    });
    auditLog.record({
      actorId: actor?.id, actorRole: actor?.role,
      action: AUDIT_ACTION.PROJECT_PUBLISH,
      targetType: 'project', targetId: projectId,
      after: { versionId: pending.id },
    });

    // ۴. Notify teacher
        const tp = dataEngine.teacher.get(project.teacherId);
    if (tp && tp.userId) {
      notificationBus.notify({
        recipientId: tp.userId,
        type: NOTIF_TYPE.VERSION_APPROVED,
        payload: {
          projectId,
          versionId: pending.id,
          grade: project.grade,
          field: project.field || null,
          subject: project.subject,
          note,
        },
      });
    }

    return { ok: true, data: { projectId, versionId: pending.id, migrationResult } };
  }
  /**
   * رد نسخه‌ی pending
   */
  rejectPendingVersion(projectId, { actor, reason = '' } = {}) {
    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, errors: ['project_not_found'] };
    if (!project.pendingVersionId) return { ok: false, errors: ['no_pending_version'] };

    const pending = dataEngine.version.get(project.pendingVersionId);
    if (!pending) return { ok: false, errors: ['pending_not_found'] };

    // mark rejected
    dataEngine.version.markRejected(pending.id, { reason, byUserId: actor?.id });
    // برگرداندن به draft (معلم می‌تونه دوباره ویرایش کنه)
    dataEngine.version.markDraft(pending.id);

    // pending را روی project پاک کن
    dataEngine.project.clearPending(projectId);
    // draftVersionId رو دوباره ست کن
    dataEngine.project.setDraft(projectId, pending.id);

    // state transition
    const targetState = project.publishedVersionId
      ? PROJECT_STATE.PUBLISHED
      : PROJECT_STATE.BUILDING;
    if (project.state !== targetState) {
      try { dataEngine.project.transitionState(projectId, targetState, { byUserId: actor?.id }); }
      catch (e) { /* ignore */ }
    }

    // audit
    auditLog.record({
      actorId: actor?.id, actorRole: actor?.role,
      action: AUDIT_ACTION.VERSION_REJECT,
      targetType: 'project', targetId: projectId,
      after: { versionId: pending.id, reason },
    });

    // notify teacher
        const tp = dataEngine.teacher.get(project.teacherId);
    if (tp && tp.userId) {
      notificationBus.notify({
        recipientId: tp.userId,
        type: NOTIF_TYPE.VERSION_REJECTED,
        payload: {
          projectId,
          versionId: pending.id,
          grade: project.grade,
          field: project.field || null,
          subject: project.subject,
          reason,
        },
      });
    }
    return { ok: true };
  }

  /**
   * درخواست اصلاح برای نسخه‌ی pending
   */
  requestEditForPendingVersion(projectId, { actor, reason = '' } = {}) {
    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, errors: ['project_not_found'] };
    if (!project.pendingVersionId) return { ok: false, errors: ['no_pending_version'] };

    const pending = dataEngine.version.get(project.pendingVersionId);
    if (!pending) return { ok: false, errors: ['pending_not_found'] };

    // برگردان به draft + ذخیره‌ی note مدیر
    dataEngine.version.markDraft(pending.id);
    dataEngine.version.update(pending.id, { adminNotes: reason });
    dataEngine.project.clearPending(projectId);
    dataEngine.project.setDraft(projectId, pending.id);

    const targetState = project.publishedVersionId
      ? PROJECT_STATE.PUBLISHED
      : PROJECT_STATE.BUILDING;
    if (project.state !== targetState) {
      try { dataEngine.project.transitionState(projectId, targetState, { byUserId: actor?.id }); }
      catch (e) { /* ignore */ }
    }

    auditLog.record({
      actorId: actor?.id, actorRole: actor?.role,
      action: AUDIT_ACTION.VERSION_EDIT_REQUEST,
      targetType: 'project', targetId: projectId,
      after: { versionId: pending.id, reason },
    });

        const tp = dataEngine.teacher.get(project.teacherId);
    if (tp && tp.userId) {
      notificationBus.notify({
        recipientId: tp.userId,
        type: NOTIF_TYPE.VERSION_EDIT_REQUESTED,
        payload: {
          projectId,
          versionId: pending.id,
          grade: project.grade,
          field: project.field || null,
          subject: project.subject,
          reason,
        },
      });
    }
    return { ok: true };
  }

  /** بازگردانی به یک نسخه‌ی خاص */
  rollbackToVersion(projectId, targetVersionId, { actor, note = '' } = {}) {
    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, errors: ['project_not_found'] };

    const target = dataEngine.version.get(targetVersionId);
    if (!target || target.projectId !== projectId) {
      return { ok: false, errors: ['version_not_found'] };
    }
    if (target.status === VERSION_STATUS.PUBLISHED) {
      return { ok: false, errors: ['already_published'] };
    }

    // ساخت نسخه‌ی جدید از روی target
    const newDraft = dataEngine.version.create({
      projectId,
      parentVersionId: targetVersionId,
      status: VERSION_STATUS.DRAFT,
      chapters: JSON.parse(JSON.stringify(target.chapters)).map(createChapter),
      createdBy: actor?.id,
      changelog: [{ type: 'rollback', from: targetVersionId, at: now() }],
    });
    if (!newDraft.ok) return newDraft;

    dataEngine.project.setDraft(projectId, newDraft.data.id);

    auditLog.record({
      actorId: actor?.id, actorRole: actor?.role,
      action: AUDIT_ACTION.VERSION_ROLLBACK,
      targetType: 'project', targetId: projectId,
      before: { publishedVersionId: project.publishedVersionId },
      after: { newDraftId: newDraft.data.id, basedOn: targetVersionId },
      note,
    });
    return { ok: true, data: newDraft.data };
  }

  _teacherName(teacherId) {
    const tp = dataEngine.teacher.get(teacherId);
    return tp?.fullName || 'معلم';
  }

  _notifyAdmins(type, payload) {
    // 🔧 FIX: خواندن مدیرها از سرور (چون کاربران در localStorage ذخیره نمی‌شوند)
    // این تابع fire-and-forget است — فراخوان‌ها نیاز به await ندارند
    (async () => {
      try {
        const users = await _api.getAll('users');
        const admins = Array.isArray(users)
          ? users.filter((u) => u && u.role === 'admin' && u.active !== false)
          : [];
        for (const admin of admins) {
          notificationBus.notify({
            recipientId: admin.id,
            type,
            payload,
          });
        }
      } catch (e) {
        console.warn('[VersionManager] notify admins failed:', e);
      }
    })();
  }
}

// ================================================================
// SECTION 11: WORKFLOW ENGINE
// ================================================================

class WorkflowEngine {
  // ---------- Teacher Request Workflow ----------

  /**
   * ثبت درخواست معلمی جدید
   */
    submitTeacherRequest({
    actor,
    grade, subject, field = null,
    formData,
    proposedFolderName = null,
    idempotencyKey = null,
  }) {
    if (!actor || actor.role !== 'teacher') {
      return { ok: false, errors: ['forbidden'] };
    }
    const g = normalizeGrade(grade);
    const s = normalizeSubject(subject);
    const f = normalizeField(field);
    if (!g || !s) return { ok: false, errors: ['invalid_grade_or_subject'] };

    // ✅ اعتبارسنجی رشته بر اساس پایه
    if (needsField(g) && !f) {
      return { ok: false, errors: ['field_required'] };
    }
    if (!needsField(g) && f) {
      return { ok: false, errors: ['field_not_allowed'] };
    }

    // rate limit
    const rl = rateLimiter.check(actor.id, 'request_submit');
    if (!rl.allowed) {
      return { ok: false, errors: ['rate_limited'], retryInMs: rl.retryInMs };
    }

    // canRequest? — با رشته
    const can = dataEngine.query.canRequestSubject(actor.id, g, s, f);
    if (!can.ok) return { ok: false, errors: [can.error], details: can };

    // generate folderName اگر نداده
    const folderName = proposedFolderName || dataEngine.utils.generateFolderName(formData?.fullName || actor.name || 'Teacher');

    // ساخت درخواست — با field
    const rqRes = dataEngine.request.create({
      userId: actor.id,
      teacherId: null,
      grade: g,
      field: f,
      subject: s,
      proposedFolderName: folderName,
      formData: formData || {},
      status: REQUEST_STATUS.DRAFT,
      idempotencyKey: idempotencyKey || utils.uuid(),
    });
    if (!rqRes.ok) return rqRes;

    // submit بلافاصله
    const subRes = dataEngine.request.submit(rqRes.data.id, { byUserId: actor.id });
    if (!subRes.ok) return subRes;

    // audit
    auditLog.record({
      actorId: actor.id, actorRole: actor.role,
      action: AUDIT_ACTION.REQUEST_SUBMIT,
      targetType: 'request', targetId: rqRes.data.id,
      after: { grade: g, field: f, subject: s, folderName },
    });

    // notify admins — با field
    this._notifyAdmins(NOTIF_TYPE.REQUEST_SUBMITTED, {
      requestId: rqRes.data.id,
      teacherName: formData?.fullName || actor.name || 'معلم',
      grade: g,
      field: f,
      subject: s,
    });

    return { ok: true, data: rqRes.data };
  }

  withdrawRequest({ actor, requestId }) {
    const rq = dataEngine.request.get(requestId);
    if (!rq) return { ok: false, errors: ['not_found'] };
    if (rq.userId !== actor.id && rq.teacherId !== actor.id) {
      return { ok: false, errors: ['forbidden'] };
    }
    const rl = rateLimiter.check(actor.id, 'request_withdraw');
    if (!rl.allowed) return { ok: false, errors: ['rate_limited'], retryInMs: rl.retryInMs };

    const res = dataEngine.request.withdraw(requestId, { byUserId: actor.id });
    if (res.ok) {
      auditLog.record({
        actorId: actor.id, actorRole: actor.role,
        action: AUDIT_ACTION.REQUEST_WITHDRAW,
        targetType: 'request', targetId: requestId,
      });
    }
    return res;
  }

  /** admin approve */
  approveRequest({ actor, requestId, adminNote = '' }) {
    permissionGuard.assert(actor, 'request.approve');
    const rq = dataEngine.request.get(requestId);
    if (!rq) return { ok: false, errors: ['not_found'] };

    // ۱. پیدا یا ساخت TeacherProfile
    let tp = rq.teacherId ? dataEngine.teacher.get(rq.teacherId) : null;
    if (!tp) tp = dataEngine.teacher.getByUserId(rq.userId);

    if (!tp) {
      // ساخت TeacherProfile جدید
      const tpRes = dataEngine.teacher.create({
        userId: rq.userId,
        folderName: rq.proposedFolderName,
        fullName: rq.formData?.fullName || '',
        bio: rq.formData?.bio || '',
        experienceYears: rq.formData?.experienceYears || 0,
        specialties: rq.formData?.specialties || [],
        avatarBlobId: rq.formData?.avatarBlobId || null,
        certificateBlobIds: rq.formData?.certificateBlobIds || [],
        socialLinks: rq.formData?.socialLinks || {},
        isActive: true,
      });
      if (!tpRes.ok) return tpRes;
      tp = tpRes.data;

      auditLog.record({
        actorId: actor.id, actorRole: actor.role,
        action: AUDIT_ACTION.TEACHER_CREATE,
        targetType: 'teacher', targetId: tp.id,
        after: { folderName: tp.folderName, userId: tp.userId },
      });
    }





    // ۲. بررسی عدم تداخل پروژه
        // ۲. بررسی عدم تداخل پروژه (با رشته)
    const existingProj = dataEngine.project.getByKey(
      rq.grade, rq.subject, tp.folderName, rq.field || null
    );
    if (existingProj) {
      return { ok: false, errors: ['project_exists'], projectId: existingProj.id };
    }

    // ۳. ساخت Project — با عنوان curriculum-aware و رشته
    const projectTitle = bookText(rq.grade, rq.field, rq.subject)
      || `${subjectText(rq.subject)} - ${gradeText(rq.grade)}`;

    const pjRes = dataEngine.project.create({
      teacherId: tp.id,
      grade: rq.grade,
      field: rq.field || null,
      subject: rq.subject,
      folderName: tp.folderName,
      title: projectTitle,
      description: '',
      state: PROJECT_STATE.BUILDING,
    });
    if (!pjRes.ok) return pjRes;

    auditLog.record({
      actorId: actor.id, actorRole: actor.role,
      action: AUDIT_ACTION.PROJECT_CREATE,
      targetType: 'project', targetId: pjRes.data.id,
      after: {
        grade: rq.grade,
        field: rq.field || null,
        subject: rq.subject,
        folderName: tp.folderName,
      },
    });

    // ۴. ساخت draft اولیه
    versionManager.createInitialDraft(pjRes.data.id, { actor: { id: actor.id, role: 'admin' } });

    // ۵. approve request
    const apRes = dataEngine.request.approve(requestId, { adminId: actor.id, note: adminNote });
    if (apRes.ok) {
      dataEngine.request.linkProject(requestId, pjRes.data.id);
    }

    auditLog.record({
      actorId: actor.id, actorRole: actor.role,
      action: AUDIT_ACTION.REQUEST_APPROVE,
      targetType: 'request', targetId: requestId,
      after: { teacherId: tp.id, projectId: pjRes.data.id },
      note: adminNote,
    });

    // notify teacher
        // notify teacher
    notificationBus.notify({
      recipientId: rq.userId,
      type: NOTIF_TYPE.REQUEST_APPROVED,
      payload: {
        requestId,
        projectId: pjRes.data.id,
        grade: rq.grade,
        field: rq.field || null,
        subject: rq.subject,
      },
    });

    return { ok: true, data: { teacher: tp, project: pjRes.data, request: apRes.data } };
  }

  rejectRequest({ actor, requestId, reason = '' }) {
    permissionGuard.assert(actor, 'request.reject');
    const rq = dataEngine.request.get(requestId);
    if (!rq) return { ok: false, errors: ['not_found'] };

    const res = dataEngine.request.reject(requestId, { adminId: actor.id, note: reason });
    if (!res.ok) return res;

    auditLog.record({
      actorId: actor.id, actorRole: actor.role,
      action: AUDIT_ACTION.REQUEST_REJECT,
      targetType: 'request', targetId: requestId,
      note: reason,
    });

        notificationBus.notify({
      recipientId: rq.userId,
      type: NOTIF_TYPE.REQUEST_REJECTED,
      payload: {
        requestId,
        reason,
        grade: rq.grade,
        field: rq.field || null,
        subject: rq.subject,
      },
    });
    return { ok: true };
  }

  requestEditForRequest({ actor, requestId, reason = '' }) {
    permissionGuard.assert(actor, 'request.edit_request');
    const rq = dataEngine.request.get(requestId);
    if (!rq) return { ok: false, errors: ['not_found'] };

    const res = dataEngine.request.requestEdit(requestId, { adminId: actor.id, note: reason });
    if (!res.ok) return res;

    auditLog.record({
      actorId: actor.id, actorRole: actor.role,
      action: AUDIT_ACTION.REQUEST_EDIT_REQUEST,
      targetType: 'request', targetId: requestId,
      note: reason,
    });

        notificationBus.notify({
      recipientId: rq.userId,
      type: NOTIF_TYPE.REQUEST_EDIT_REQUESTED,
      payload: {
        requestId,
        reason,
        grade: rq.grade,
        field: rq.field || null,
        subject: rq.subject,
      },
    });
    return { ok: true };
  }

  /** admin edit (ویرایش درخواست توسط مدیر) */
  adminEditRequest({ actor, requestId, patch }) {
    permissionGuard.assert(actor, 'request.edit', { requestId });
    const rq = dataEngine.request.get(requestId);
    if (!rq) return { ok: false, errors: ['not_found'] };

    const before = JSON.parse(JSON.stringify(rq));

    // اگر می‌خواد grade/subject/folderName عوض کنه
        // اگر می‌خواد grade/subject/field/folderName عوض کنه
    if (patch.grade || patch.subject || patch.field !== undefined || patch.proposedFolderName) {
      const res = dataEngine.request.updateProposed(requestId, patch);
      if (!res.ok) return res;
    }
    if (patch.formData) {
      const res = dataEngine.request.updateFormData(requestId, patch.formData);
      if (!res.ok) return res;
    }

    auditLog.record({
      actorId: actor.id, actorRole: actor.role,
      action: AUDIT_ACTION.REQUEST_EDIT,
      targetType: 'request', targetId: requestId,
      before, after: patch,
    });

    return { ok: true, data: dataEngine.request.get(requestId) };
  }

  // ---------- Update Request (after publish) ----------

  /** معلم درخواست بروزرسانی می‌دهد */
  submitUpdateRequest({ actor, projectId, changelog = [], note = '' }) {
    if (!actor || actor.role !== 'teacher') return { ok: false, errors: ['forbidden'] };

    const rl = rateLimiter.check(actor.id, 'project_submit');
    if (!rl.allowed) return { ok: false, errors: ['rate_limited'], retryInMs: rl.retryInMs };

    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, errors: ['project_not_found'] };
    const tp = dataEngine.teacher.get(project.teacherId);
    if (!tp || tp.userId !== actor.id) return { ok: false, errors: ['forbidden'] };
    if (project.state === PROJECT_STATE.UPDATE_PENDING) {
      return { ok: false, errors: ['already_pending'] };
    }

    const res = versionManager.submitForReview(projectId, { actor, changelog, note });
    if (!res.ok) return res;

    // notify admins
    // (این در خود versionManager انجام شده)

    return res;
  }

  // ---------- Common helpers ----------

  _notifyAdmins(type, payload) {
    // 🔧 FIX: خواندن مدیرها از سرور (چون کاربران در localStorage ذخیره نمی‌شوند)
    // این تابع fire-and-forget است — فراخوان‌ها نیاز به await ندارند
    (async () => {
      try {
        const users = await _api.getAll('users');
        const admins = Array.isArray(users)
          ? users.filter((u) => u && u.role === 'admin' && u.active !== false)
          : [];
        for (const admin of admins) {
          notificationBus.notify({
            recipientId: admin.id,
            type,
            payload,
          });
        }
      } catch (e) {
        console.warn('[Workflow] notify admins failed:', e);
      }
    })();
  }

    _gradeLabel(grade) {
    return gradeText(grade);
  }

  _subjectLabel(subject) {
    return subjectText(subject);
  }
}

// ================================================================
// SECTION 12: ROLLBACK MANAGER
// ================================================================

class RollbackManager {
  /**
   * بازگردانی کامل یک پروژه به یک نسخه‌ی مشخص
   * - draft جدید از روی آن نسخه ساخته می‌شود
   * - اگر معلم درخواست publish کند، مدیر باید تایید کند
   */
  rollbackProjectToVersion({ actor, projectId, targetVersionId, note = '' }) {
    permissionGuard.assert(actor, 'version.rollback');
    return versionManager.rollbackToVersion(projectId, targetVersionId, { actor, note });
  }

  /** بازگردانی یک migration پیشرفت */
  rollbackProgressMigration({ actor, backupId }) {
    permissionGuard.assert(actor, 'version.rollback');
    const res = progressManager.rollbackMigration(backupId);
    if (res.ok) {
      auditLog.record({
        actorId: actor.id, actorRole: actor.role,
        action: 'progress.rollback',
        targetType: 'backup', targetId: backupId,
        after: { restoredCount: res.restoredCount },
      });
    }
    return res;
  }

  /** بازگردانی یک درخواست ردشده به draft */
  reopenRejectedRequest({ actor, requestId }) {
    permissionGuard.assert(actor, 'request.edit', { requestId });
    const rq = dataEngine.request.get(requestId);
    if (!rq) return { ok: false, errors: ['not_found'] };
    if (rq.status !== REQUEST_STATUS.REJECTED) return { ok: false, errors: ['not_rejected'] };
    // اجازه‌ی transition از rejected به draft
    const res = dataEngine.request['_transition'](requestId, REQUEST_STATUS.DRAFT, {
      byUserId: actor.id, note: 'reopened by admin',
    });
    if (!res.ok) return res;

    auditLog.record({
      actorId: actor.id, actorRole: actor.role,
      action: 'request.reopen',
      targetType: 'request', targetId: requestId,
    });
    return res;
  }
}

// ================================================================
// SECTION 13: LIFECYCLE (periodic tasks)
// ================================================================

class Lifecycle {
  constructor() {
    this._interval = null;
  }

  start() {
    if (this._interval) return;
    // هر ۶ ساعت یک‌بار پاک‌سازی
    this._interval = setInterval(() => this.runCleanup(), 6 * 60 * 60 * 1000);
    // اجرای اولیه با تأخیر
    setTimeout(() => this.runCleanup(), 30000);
  }

  stop() {
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = null;
    }
  }

  runCleanup() {
    const report = { at: now(), actions: {} };
    try { report.actions.notifications = notificationBus.purgeExpired(); } catch (e) { report.actions.notifications = { error: String(e) }; }
    try { report.actions.audit = auditLog.list({ limit: 1 }).length; } catch {}
    try { report.actions.orphans = orphanArchive.purgeExpired(); } catch (e) { report.actions.orphans = { error: String(e) }; }
    try { report.actions.quota = engine.quota.periodicCleanup(); } catch {}
    return report;
  }
}

// ================================================================
// SECTION 14: SINGLETONS + FACADE
// ================================================================

export const notificationBus = new NotificationBus();
export const auditLog = new AuditLog();
export const rateLimiter = new RateLimiter();
export const permissionGuard = new PermissionGuard();
export const diffEngine = new DiffEngine();
export const impactAnalyzer = new ImpactAnalyzer();
export const progressManager = new ProgressManager();
export const orphanArchive = new OrphanArchive();
export const versionManager = new VersionManager();
export const workflowEngine = new WorkflowEngine();
export const rollbackManager = new RollbackManager();
export const lifecycle = new Lifecycle();

// اتصال periodic cleanup به engine
if (typeof window !== 'undefined') {
  try { lifecycle.start(); } catch (e) { console.warn('[Lifecycle] start failed:', e); }
  window.addEventListener('beforeunload', () => {
    try { auditLog.flush(); } catch {}
    try { lifecycle.stop(); } catch {}
  });
}

class ServerQuery {
  async getAllLessonRecords() {
    const list = await _api.getAll('lessonRecords');
    if (!Array.isArray(list)) return {};
    const out = {};
    for (const rec of list) {
      if (!rec || !rec.data) continue;
      const m = rec.key?.match(/^lesson-records-(.+)$/);
      if (!m) continue;
      out[m[1]] = rec.data;
    }
    return out;
  }

  async getAllLessonProgress() {
    const list = await _api.getAll('lessonProgress');
    if (!Array.isArray(list)) return {};
    const out = {};
    for (const rec of list) {
      if (!rec || !rec.key) continue;
      const userId = rec._userId;
      if (!userId) continue;
      if (!out[userId]) out[userId] = {};
      out[userId][rec.key] = rec.data;
    }
    return out;
  }

  async getProjectSubscriptions(projectId) {
    const list = await _api.getAll('subscriptions', `projectId=${encodeURIComponent(projectId)}`);
    return Array.isArray(list) ? list : [];
  }

  async getProjectStats(projectId) {
    const [subs, progress] = await Promise.all([
      this.getProjectSubscriptions(projectId),
      this.getAllLessonProgress(),
    ]);
    const activeSubs = subs.filter((s) => s.status === 'active' && s.expiresAt > Date.now());
    const uniqueStudents = new Set();
    for (const [userId, map] of Object.entries(progress)) {
      for (const k of Object.keys(map)) {
        if (k.includes(`-${projectId}`)) { uniqueStudents.add(userId); break; }
      }
    }
    return {
      subscribers: subs.length,
      activeSubscribers: activeSubs.length,
      studentsWithProgress: uniqueStudents.size,
    };
  }
}

export const serverQuery = new ServerQuery();

export const logicEngine = {
  notif: notificationBus,
  audit: auditLog,
  rate: rateLimiter,
  perm: permissionGuard,
  diff: diffEngine,
  impact: impactAnalyzer,
  progress: progressManager,
  subscription: subscriptionManager,
  serverQuery,
  orphan: orphanArchive,
  version: versionManager,
  workflow: workflowEngine,
  rollback: rollbackManager,
  lifecycle,
  NOTIF_TYPE,
  AUDIT_ACTION,
  PROGRESS_SCHEMA_VERSION,
  _internal: { RATE_LIMITS, RETENTION, PERMISSIONS },
};

export default logicEngine;